import React, { useState, useEffect, useRef, useMemo } from 'react';
import Layout from '../../components/Layout';
import SiteMap, { DEFAULT_CAMERAS, DEFAULT_OUTPUTS, DEFAULT_ALARM_ZONES, MapCamera } from './SiteMap';
import { usePageCustomizer, CustomizeButton, CustomizerPanel, PageWidget } from '../../components/PageCustomizer';
import { exportCSV, csvDateStamp } from '../../utils/exportCSV';
import { useLicense } from '../../context/LicenseContext';
import { useSite } from '../../context/SiteContext';
import { useToast } from '../../context/ToastContext';
import EmptyState from '../../components/EmptyState';

const ACCESS_WIDGETS: PageWidget[] = [
  { id:'map',        label:'Site Map',          icon:'🗺️', description:'Interactive map showing door locations', defaultVisible:true },
  { id:'events',     label:'Access Events',     icon:'📋', description:'Door events, access log and audit trail', defaultVisible:true },
  { id:'doors',      label:'Doors & Commands',  icon:'🚪', description:'Door status and remote control commands', defaultVisible:true },
  { id:'attendance', label:'Attendance',        icon:'👥', description:'Who is badged in at each site', defaultVisible:true },
  { id:'intercom',   label:'Intercom',          icon:'📞', description:'Intercom stations and call log', defaultVisible:true },
  { id:'users',      label:'Users',             icon:'👤', description:'User and credential management', defaultVisible:true },
];

// ─── Intercom seed data ───────────────────────────────────────────────────────

interface IntercomStation {
  id: string; name: string; site: string; type: string;
  status: 'online'|'offline'|'busy'|'ringing'; lastCall: string; zone: string;
}

const STATIONS: IntercomStation[] = [
  { id:'AQ-MAIN',   name:'Aquatic Centre Main Entry', site:'Aquatic Centre',   type:'Jacques IP Video', status:'online',   lastCall:'09:58', zone:'lobby' },
  { id:'TH-FRONT',  name:'Town Hall Front Desk',      site:'Town Hall',        type:'Jacques IP Video', status:'ringing',  lastCall:'Just now', zone:'lobby' },
  { id:'CC-ENTRY',  name:'Civic Centre Entry',        site:'Civic Centre',     type:'Jacques IP Audio', status:'online',   lastCall:'08:41', zone:'corridor' },
  { id:'DEP-GATE',  name:'Depot Security Gate',       site:'Council Depot',    type:'Jacques IP Video', status:'online',   lastCall:'07:30', zone:'carpark' },
  { id:'PS-LOBBY',  name:'Parramatta Sq Lobby',       site:'Parramatta Square',type:'Jacques IP Video', status:'offline',  lastCall:'Yesterday', zone:'lobby' },
  { id:'TH-LIFT',   name:'Town Hall Lift Lobby',      site:'Town Hall',        type:'Jacques IP Audio', status:'online',   lastCall:'06:45', zone:'corridor' },
];

interface CallLog {
  id: number; station: string; site: string; time: string; duration: string;
  direction: 'inbound'|'outbound'|'missed'; answered: boolean;
}

const CALL_LOG: CallLog[] = [
  { id:1, station:'TH-FRONT',  site:'Town Hall',        time:'Just now', duration:'—',    direction:'inbound',  answered:false },
  { id:2, station:'AQ-MAIN',   site:'Aquatic Centre',   time:'09:58',    duration:'0:45', direction:'inbound',  answered:false },
  { id:3, station:'CC-ENTRY',  site:'Civic Centre',     time:'08:41',    duration:'1:12', direction:'inbound',  answered:true  },
  { id:4, station:'TH-FRONT',  site:'Town Hall',        time:'08:15',    duration:'0:32', direction:'inbound',  answered:true  },
  { id:5, station:'DEP-GATE',  site:'Council Depot',    time:'07:30',    duration:'0:58', direction:'inbound',  answered:true  },
  { id:6, station:'TH-LIFT',   site:'Town Hall',        time:'06:45',    duration:'0:22', direction:'inbound',  answered:true  },
];

// ── Controllers (Protege GX) ──────────────────────────────────────────────────
const CONTROLLERS = [
  { id:'CTL-01', name:'Town Hall Main Controller',    site:'Town Hall',        ip:'10.10.1.11', doors:3, readers:6, status:'online',  firmware:'v3.4.0', lastSeen:'Just now',    health:'OK' },
  { id:'CTL-02', name:'Civic Centre Controller',      site:'Civic Centre',     ip:'10.10.2.11', doors:3, readers:5, status:'online',  firmware:'v3.4.0', lastSeen:'Just now',    health:'OK' },
  { id:'CTL-03', name:'Aquatic Centre Controller',    site:'Aquatic Centre',   ip:'10.10.3.11', doors:2, readers:3, status:'online',  firmware:'v3.3.2', lastSeen:'2 min ago',   health:'Firmware outdated' },
  { id:'CTL-04', name:'Council Depot Controller',     site:'Council Depot',    ip:'10.10.4.11', doors:2, readers:4, status:'online',  firmware:'v3.4.0', lastSeen:'Just now',    health:'OK' },
  { id:'CTL-05', name:'Parramatta Square Controller', site:'Parramatta Square',ip:'10.10.5.11', doors:1, readers:2, status:'offline', firmware:'v3.2.1', lastSeen:'14 min ago',  health:'Offline' },
];

// ── Doors ─────────────────────────────────────────────────────────────────────
interface Door {
  id: number; name: string; site: string; controller: string;
  status: 'locked'|'unlocked'|'open'|'forced-open'|'tamper';
  mode: 'scheduled'|'manual-lock'|'manual-unlock'|'forced';
  schedule: string; lastEvent: string; lastEventTime: string;
  health: 'ok'|'tamper'|'fault';
  readerEnabled: boolean;
}

const DOORS: Door[] = [
  { id:1, name:'Main Entry',       site:'Town Hall',        controller:'CTL-01', status:'unlocked',    mode:'scheduled',      schedule:'Mon–Fri 08:00–18:00', lastEvent:'Access Granted — S. Mitchell',     lastEventTime:'08:41', health:'ok',     readerEnabled:true },
  { id:2, name:'Server Room B2',   site:'Civic Centre',     controller:'CTL-02', status:'locked',      mode:'manual-lock',    schedule:'Restricted 24/7',     lastEvent:'Access Denied — Unknown',          lastEventTime:'08:39', health:'ok',     readerEnabled:true },
  { id:3, name:'Staff Gate',       site:'Aquatic Centre',   controller:'CTL-03', status:'unlocked',    mode:'scheduled',      schedule:'Mon–Sun 06:00–21:00', lastEvent:'Access Granted — J. Okafor',       lastEventTime:'08:35', health:'ok',     readerEnabled:true },
  { id:4, name:'Control Room',     site:'Town Hall',        controller:'CTL-01', status:'locked',      mode:'scheduled',      schedule:'Restricted 24/7',     lastEvent:'Override — Admin PIN',             lastEventTime:'08:21', health:'ok',     readerEnabled:true },
  { id:5, name:'Depot Rear Gate',  site:'Council Depot',    controller:'CTL-04', status:'forced-open', mode:'forced',         schedule:'Mon–Fri 07:00–17:00', lastEvent:'FORCED OPEN — External',           lastEventTime:'08:18', health:'ok',     readerEnabled:true },
  { id:6, name:'Reception',        site:'Civic Centre',     controller:'CTL-02', status:'unlocked',    mode:'scheduled',      schedule:'Mon–Fri 08:00–17:30', lastEvent:'Access Granted — W. Zhang',        lastEventTime:'07:44', health:'ok',     readerEnabled:true },
  { id:7, name:'Roof Access',      site:'Parramatta Square',controller:'CTL-05', status:'locked',      mode:'manual-lock',    schedule:'Restricted 24/7',     lastEvent:'Access Denied — Unknown',          lastEventTime:'07:18', health:'tamper', readerEnabled:true },
  { id:8, name:'Electrical Room',  site:'Town Hall',        controller:'CTL-01', status:'locked',      mode:'scheduled',      schedule:'Restricted 24/7',     lastEvent:'Access Granted — Admin Override',  lastEventTime:'06:30', health:'ok',     readerEnabled:true },
];

// ── Access Events ─────────────────────────────────────────────────────────────
const EVENTS = [
  { id:1,  user:'Sarah Mitchell',  card:'***4421', door:'Main Entry',      site:'Town Hall',        time:'08:41:22', result:'granted' },
  { id:2,  user:'Unknown',         card:'***0012', door:'Server Room B2',  site:'Civic Centre',     time:'08:39:05', result:'denied' },
  { id:3,  user:'James Okafor',    card:'***7890', door:'Staff Gate',      site:'Aquatic Centre',   time:'08:35:11', result:'granted' },
  { id:4,  user:'Admin Override',  card:'PIN',     door:'Control Room',    site:'Town Hall',        time:'08:21:44', result:'granted' },
  { id:5,  user:'Unknown',         card:'***3311', door:'Depot Rear Gate', site:'Council Depot',    time:'07:58:03', result:'denied' },
  { id:6,  user:'Wei Zhang',       card:'***2244', door:'Staff Lift L3',   site:'Civic Centre',     time:'07:44:22', result:'granted' },
  { id:7,  user:'Priya Sharma',    card:'***9981', door:'Archive Room',    site:'Town Hall',        time:'07:30:09', result:'granted' },
  { id:8,  user:'Unknown',         card:'***0000', door:'Roof Access',     site:'Parramatta Square',time:'07:18:55', result:'denied' },
  { id:9,  user:'Tom Nguyen',      card:'***5512', door:'Main Entry',      site:'Council Depot',    time:'07:02:40', result:'granted' },
  { id:10, user:'Lisa Park',       card:'***6671', door:'Staff Gate',      site:'River Walk',       time:'06:58:01', result:'granted' },
];

// ── Attendance data ───────────────────────────────────────────────────────────
const ATTENDANCE = [
  { name:'Sarah Mitchell', dept:'Security',     badgeIn:'07:58', badgeOut:'—',     duration:'Active', status:'in' },
  { name:'James Okafor',   dept:'Operations',   badgeIn:'08:00', badgeOut:'—',     duration:'Active', status:'in' },
  { name:'Wei Zhang',      dept:'ICT',          badgeIn:'08:12', badgeOut:'—',     duration:'Active', status:'in' },
  { name:'Priya Sharma',   dept:'Admin',        badgeIn:'08:30', badgeOut:'—',     duration:'Active', status:'in' },
  { name:'Tom Nguyen',     dept:'Operations',   badgeIn:'07:00', badgeOut:'—',     duration:'Active', status:'in' },
  { name:'Lisa Park',      dept:'HR',           badgeIn:'08:45', badgeOut:'—',     duration:'Active', status:'in' },
  { name:'John Wallace',   dept:'Facilities',   badgeIn:'07:30', badgeOut:'12:15', duration:'4h 45m', status:'out' },
  { name:'Amy Chen',       dept:'Finance',      badgeIn:'08:55', badgeOut:'—',     duration:'Active', status:'in' },
  { name:'Marcus Reid',    dept:'Security',     badgeIn:'06:00', badgeOut:'14:00', duration:'8h 00m', status:'out' },
  { name:'Olivia Brown',   dept:'Legal',        badgeIn:'09:10', badgeOut:'—',     duration:'Active', status:'in' },
];

const HOURLY = [
  { h:'06:00', count:3 }, { h:'07:00', count:8 }, { h:'08:00', count:22 },
  { h:'09:00', count:31 }, { h:'10:00', count:34 }, { h:'11:00', count:34 },
  { h:'12:00', count:30 }, { h:'13:00', count:28 }, { h:'14:00', count:26 },
];
// ── Areas seed (visual only) ──────────────────────────────────────────────────
interface AreaSeed { id:string; name:string; site:string; status:'armed'|'disarmed'|'alarmed'; lastBy:string; lastAt:string; }
const AREAS_SEED: AreaSeed[] = [
  { id:'a1', name:'Main Entry Zone',    site:'Town Hall',        status:'disarmed', lastBy:'S. Mitchell', lastAt:'08:41' },
  { id:'a2', name:'Server Room B2',     site:'Civic Centre',     status:'armed',    lastBy:'Admin',       lastAt:'18:00' },
  { id:'a3', name:'Operations Centre',  site:'Town Hall',        status:'disarmed', lastBy:'J. Okafor',   lastAt:'07:55' },
  { id:'a4', name:'Car Park Level 1',   site:'Civic Centre',     status:'disarmed', lastBy:'Auto',        lastAt:'06:00' },
  { id:'a5', name:'Staff Offices',      site:'Aquatic Centre',   status:'armed',    lastBy:'Admin',       lastAt:'17:30' },
  { id:'a6', name:'Control Room',       site:'Town Hall',        status:'alarmed',  lastBy:'System',      lastAt:'08:18' },
];

// ── Door status + mode colors ─────────────────────────────────────────────────
const DOOR_STATUS_COLOR: Record<string,string> = {
  'locked':'#555555', 'unlocked':'#22c55e', 'open':'#3b82f6', 'forced-open':'#ef4444', 'tamper':'#ef4444'
};
const DOOR_MODE_COLOR: Record<string,string> = {
  'scheduled':'#3b82f6', 'manual-lock':'#555555', 'manual-unlock':'#22c55e', 'forced':'#ef4444'
};

// ── Door command button ───────────────────────────────────────────────────────
function DoorCommandBtn({ label, color, onClick }: { label:string; color:string; onClick:()=>void }) {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  function handle() {
    setLoading(true);
    setTimeout(() => { setLoading(false); setDone(true); setTimeout(()=>setDone(false),2000); }, 1200);
  }
  return (
    <button onClick={handle}
      disabled={loading}
      style={{
        padding:'5px 10px', borderRadius:'6px', fontSize:'10px', fontWeight:700,
        border:`1px solid ${done?'rgba(34,197,94,0.3)':`${color}30`}`,
        background:done?'rgba(34,197,94,0.1)':`${color}12`,
        color:done?'#22c55e':color, cursor:loading?'wait':'pointer',
        transition:'all 0.2s', whiteSpace:'nowrap',
      }}
    >
      {loading?'Sending...' : done?'Sent' : label}
    </button>
  );
}

// ── Protege GX live data types ────────────────────────────────────────────────
interface ProtegeEvent { id:string; eventDesc:string; deviceName:string; userName:string; timestamp:string; eventType:string; }
interface ProtegeDoor  { id:string; name:string; name2:string; siteId:string; status:string; controller?:string; controllerId?:string; }
interface ProtegeCard  { id:string; name:string; name2:string; department:string; }

function apiGet(path: string, token: string) {
  return fetch(`/api${path}`, { headers: { Authorization: `Bearer ${token}` } });
}
const getToken = () => localStorage.getItem('lemai_token') || '';

// ── Camera Feed Popup ──────────────────────────────────────────────────────────
interface CameraFeedProps { id: string; name: string; status: string; onClose: () => void; }
function CameraFeedPopup({ id, name, status, onClose }: CameraFeedProps) {
  const statusColor = status === 'online' ? '#22c55e' : status === 'motion' ? '#f59e0b' : status === 'alarm' ? '#ef4444' : '#555555';
  const statusLabel = status === 'online' ? 'LIVE' : status === 'motion' ? 'MOTION' : status === 'alarm' ? 'ALARM' : 'OFFLINE';
  return (
    <div style={{
      position:'fixed', bottom:'24px', right:'24px', width:'340px',
      background:'rgba(8,12,22,0.97)', border:`1px solid ${statusColor}40`,
      borderRadius:'5px', overflow:'hidden', zIndex:9000,
      boxShadow:`0 8px 32px rgba(0,0,0,0.6), 0 0 0 1px ${statusColor}20`,
    }}>
      {/* Header */}
      <div style={{display:'flex',alignItems:'center',gap:'8px',padding:'10px 14px',background:'#111',borderBottom:`1px solid ${statusColor}25`}}>
        <div style={{width:8,height:8,borderRadius:'50%',background:statusColor,boxShadow:`0 0 6px ${statusColor}`,flexShrink:0}}/>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:'12px',fontWeight:700,color:'#e5e5e5',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{name}</div>
          <div style={{fontSize:'10px',color:'#555555',marginTop:'1px'}}>{id}</div>
        </div>
        <span style={{fontSize:'9px',fontWeight:700,padding:'2px 7px',borderRadius:'5px',background:`${statusColor}18`,color:statusColor}}>{statusLabel}</span>
        <button onClick={onClose} style={{background:'none',border:'none',color:'#555555',cursor:'pointer',fontSize:'16px',lineHeight:1,padding:'0 2px',marginLeft:'4px'}}>✕</button>
      </div>
      {/* Feed area */}
      <div style={{position:'relative',background:'#000',aspectRatio:'16/9',display:'flex',alignItems:'center',justifyContent:'center'}}>
        {status === 'offline' ? (
          <div style={{textAlign:'center',color:'#555555'}}>
            <div style={{fontSize:'28px',marginBottom:'8px'}}>📷</div>
            <div style={{fontSize:'11px',fontWeight:600}}>Camera Offline</div>
            <div style={{fontSize:'10px',marginTop:'4px',color:'#444444'}}>Check device connection</div>
          </div>
        ) : (
          <>
            {/* Scanline overlay for realism */}
            <div style={{position:'absolute',inset:0,backgroundImage:'repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(0,0,0,0.08) 2px,rgba(0,0,0,0.08) 4px)',pointerEvents:'none',zIndex:2}}/>
            {/* Corner brackets (security camera UI aesthetic) */}
            {[{t:0,l:0,bt:'border-top',bl:'border-left'},{t:0,r:0,bt:'border-top',bl:'border-right'},{b:0,l:0,bt:'border-bottom',bl:'border-left'},{b:0,r:0,bt:'border-bottom',bl:'border-right'}].map((_c,i) => (
              <div key={i} style={{position:'absolute',top:_c.t!==undefined?8:undefined,bottom:_c.b!==undefined?8:undefined,left:_c.l!==undefined?8:undefined,right:_c.r!==undefined?8:undefined,width:14,height:14,borderColor:`${statusColor}80`,borderStyle:'solid',borderWidth:0,[_c.bt]:2,[_c.bl]:2,zIndex:3}}/>
            ))}
            {/* Timestamp */}
            <div style={{position:'absolute',top:8,left:22,fontSize:'9px',color:'rgba(255,255,255,0.6)',fontFamily:'monospace',zIndex:4,letterSpacing:'0.05em'}}>
              {new Date().toLocaleTimeString('en-AU',{hour12:false})} · {id}
            </div>
            {/* Status motion indicator */}
            {status === 'motion' && (
              <div style={{position:'absolute',top:8,right:22,fontSize:'9px',color:'#f59e0b',fontWeight:700,zIndex:4,animation:'pulse 1s ease-in-out infinite'}}>⬟ MOTION</div>
            )}
            {status === 'alarm' && (
              <div style={{position:'absolute',top:8,right:22,fontSize:'9px',color:'#ef4444',fontWeight:700,zIndex:4,animation:'pulse 1s ease-in-out infinite'}}>⚠ ALARM</div>
            )}
            {/* Feed placeholder with camera icon + connecting text */}
            <div style={{textAlign:'center',color:'#444444',zIndex:1,position:'relative'}}>
              <div style={{fontSize:'36px',marginBottom:'8px',opacity:0.25}}>📹</div>
              <div style={{fontSize:'10px',color:'#555555'}}>Stream not configured</div>
              <div style={{fontSize:'9px',color:'#444444',marginTop:'4px'}}>Configure RTSP URL per camera in settings</div>
            </div>
          </>
        )}
      </div>
      {/* Controls bar */}
      <div style={{display:'flex',gap:'6px',padding:'10px 12px',background:'rgba(0,0,0,0.3)',borderTop:'1px solid rgba(255,255,255,0.04)'}}>
        <button onClick={()=>{ window.location.href='/m/video'; }}
          style={{flex:1,padding:'5px',borderRadius:'7px',border:'1px solid rgba(59,130,246,0.25)',background:'rgba(59,130,246,0.1)',color:'#3b82f6',fontSize:'10px',fontWeight:600,cursor:'pointer'}}>
          Open Video
        </button>
        <button onClick={()=>{ const img = document.querySelector('.camera-feed-popup-img') as HTMLImageElement; if(img){ const c=document.createElement('canvas'); c.width=img.naturalWidth||img.width||320; c.height=img.naturalHeight||img.height||240; c.getContext('2d')?.drawImage(img,0,0); const a=document.createElement('a'); a.download='snapshot_'+Date.now()+'.png'; a.href=c.toDataURL(); a.click(); } else { alert('No camera feed available to snapshot.'); } }}
          style={{flex:1,padding:'5px',borderRadius:'7px',border:'1px solid rgba(245,158,11,0.25)',background:'rgba(245,158,11,0.08)',color:'#f59e0b',fontSize:'10px',fontWeight:600,cursor:'pointer'}}>
          Snapshot
        </button>
        <button onClick={onClose}
          style={{padding:'5px 10px',borderRadius:'7px',border:'1px solid #1a1a1a',background:'#111111',color:'#555555',fontSize:'10px',fontWeight:600,cursor:'pointer'}}>
          Close
        </button>
      </div>
    </div>
  );
}

export default function AccessControl() {
  const { isDemoOrg, isProtegeOrg } = useLicense();
  const { activeSiteId, activeSite } = useSite();
  const seedEvents     = useMemo(() => isDemoOrg ? EVENTS     : [], [isDemoOrg]);
  const attendance     = useMemo(() => isDemoOrg ? ATTENDANCE : [], [isDemoOrg]);
  const stations       = useMemo(() => isDemoOrg ? STATIONS   : [], [isDemoOrg]);
  const callLog        = useMemo(() => isDemoOrg ? CALL_LOG   : [], [isDemoOrg]);
  const seedControllers = useMemo(() => isDemoOrg ? CONTROLLERS : [], [isDemoOrg]);
  const seedDoors      = useMemo(() => isDemoOrg ? DOORS      : [], [isDemoOrg]);
  const hourly         = useMemo(() => isDemoOrg ? HOURLY     : [], [isDemoOrg]);

  // Controller drill-down state
  const [showControllerList, setShowControllerList] = useState(false);
  const [activeController,   setActiveController]   = useState<{id:string;name:string;online:boolean}|null>(null);
  const [liveControllers,    setLiveControllers]    = useState<{id:string;name:string;online:boolean;siteId:string}[]>([]);

  // Live Protege GX data
  const [protegeStatus,       setProtegeStatus]       = useState<'loading'|'connected'|'error'>('loading');
  const [protegeVersion,      setProtegeVersion]      = useState<string>('');
  const [protegeEvents,       setProtegeEvents]       = useState<ProtegeEvent[]>([]);
  const [protegeDoors,        setProtegeDoors]        = useState<ProtegeDoor[]>([]);
  const [protegeCards,        setProtegeCards]        = useState<ProtegeCard[]>([]);
  const [protegeCardsLoading, setProtegeCardsLoading] = useState(true);
  const [protegeAlLoading,    setProtegeAlLoading]    = useState(true);
  const [protegeInfo,         setProtegeInfo]         = useState<{siteCount:number;doorCount:number;cardholderCount:number;controllerCount:number;controllersOnline?:number;accessLevelCount?:number;scheduleCount?:number;sites?:{id:string;name:string}[]}|null>(null);
  const [protegeAccessLevels, setProtegeAccessLevels] = useState<{id:string;name:string}[]>([]);
  const [showAccessLevels,    setShowAccessLevels]    = useState(false);
  const [alSearch,            setAlSearch]            = useState('');
  const [selectedAl,          setSelectedAl]          = useState<{id:string;name:string}|null>(null);
  const [alUsers,             setAlUsers]             = useState<{id:string;name:string;name2:string;department:string}[]>([]);
  const [alUsersLoading,      setAlUsersLoading]      = useState(false);

  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setInitialLoading(false), 0);
    // Check for SOC Dashboard map-focus handoff
    const raw = sessionStorage.getItem('lemai_map_focus');
    if (raw) {
      try {
        const focus = JSON.parse(raw);
        sessionStorage.removeItem('lemai_map_focus');
        setMapFocusBanner(focus);
        setTab('map');
      } catch {}
    }
    return () => clearTimeout(t);
  }, []);

  // Fetch live Protege GX data — only when viewing City of Parramatta org
  useEffect(() => {
    if (!isProtegeOrg) {
      setProtegeStatus('disconnected' as any);
      setProtegeEvents([]);
      setProtegeDoors([]);
      setProtegeInfo(null);
      setProtegeCardsLoading(false);
      setProtegeAlLoading(false);
      return;
    }
    const token = getToken();
    if (!token) { setProtegeStatus('error'); setProtegeCardsLoading(false); setProtegeAlLoading(false); return; }

    setProtegeStatus('loading');

    // Controllers — fire first, independently, so the tile populates as fast as possible
    apiGet('/protege/controllers', token).then(r => r.ok ? r.json() : null).then(d => {
      if (d?.controllers) setLiveControllers(d.controllers);
    }).catch(()=>{});

    // Cardholders — fire independently
    apiGet('/protege/cardholders', token).then(r => r.ok ? r.json() : null).then(d => {
      if (d?.cardholders) setProtegeCards(d.cardholders);
    }).catch(()=>{}).finally(() => setProtegeCardsLoading(false));

    // Status — fire independently so connection banner shows immediately
    apiGet('/protege/status', token).then(async r => {
      if (r.ok) {
        const sd = await r.json();
        setProtegeStatus(sd.connected ? 'connected' : 'error');
        setProtegeVersion(sd.version || '');
      } else {
        setProtegeStatus('error');
      }
    }).catch(() => setProtegeStatus('error'));

    // Events + doors + system-info in parallel (these are slower)
    apiGet('/protege/events?count=50', token).then(async r => {
      if (r.ok) { const d = await r.json(); setProtegeEvents(d.events || []); }
    }).catch(()=>{});
    apiGet('/protege/doors', token).then(async r => {
      if (r.ok) { const d = await r.json(); setProtegeDoors(d.doors || []); }
    }).catch(()=>{});
    apiGet('/protege/system-info', token).then(async r => {
      if (r.ok) { const d = await r.json(); setProtegeInfo(d); }
    }).catch(()=>{});
    apiGet('/protege/access-levels', token).then(async r => {
      if (r.ok) { const d = await r.json(); setProtegeAccessLevels(d.accessLevels || []); }
    }).catch(()=>{}).finally(() => setProtegeAlLoading(false));

    // Poll events every 30s
    const interval = setInterval(() => {
      const t2 = getToken();
      if (!t2) return;
      apiGet('/protege/events/live', t2)
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d?.events?.length) setProtegeEvents(d.events); })
        .catch(() => {});
    }, 30000);
    return () => clearInterval(interval);
  }, [isProtegeOrg]);

  // Merge: prefer live Protege data, fall back to seed for demo orgs
  const events      = protegeEvents.length > 0 ? protegeEvents.map(e => ({
    id: Number(e.id) || 0,
    user: e.userName || 'Unknown',
    card: '—',
    door: e.deviceName || '—',
    site: '—',
    time: e.timestamp || '—',
    result: (e.eventType?.toLowerCase().includes('deny') || e.eventType?.toLowerCase().includes('denied')) ? 'denied' : 'granted',
  })) : seedEvents;

  const liveDoors = protegeDoors.length > 0 ? protegeDoors.map(d => ({
    id: Number(d.id) || 0,
    name: d.name,
    site: d.name2 || '—',
    controller: d.controller || '—',
    controllerId: d.controllerId || '',
    status: (d.status === 'open' ? 'open' : d.status === 'unlocked' ? 'unlocked' : 'locked') as Door['status'],
    mode: 'scheduled' as Door['mode'],
    schedule: '—',
    lastEvent: '—',
    lastEventTime: '—',
    health: 'ok' as 'ok',
    readerEnabled: true,
  })) : seedDoors;

  const controllers = seedControllers; // Use seed controllers (structural data)

  // ── Site + controller filtering ───────────────────────────────────────────────
  const siteFilterName = activeSite?.name ?? null;

  // For demo: filter seed data by site name. For Protege: data is already site-scoped.
  const filteredControllers = (!siteFilterName || isProtegeOrg) ? controllers
    : controllers.filter(c => c.site === siteFilterName);

  // Controller drill-down: filter doors/events to active controller
  const filteredDoors = (() => {
    let d = liveDoors;
    if (!isProtegeOrg && siteFilterName) d = d.filter(x => x.site === siteFilterName);
    if (activeController) d = d.filter(x => (x as any).controllerId === activeController.id || (x as any).controller === activeController.name);
    return d;
  })();

  const filteredEvents = (() => {
    let e = events;
    if (!isProtegeOrg && siteFilterName) e = e.filter(x => x.site === siteFilterName);
    return e;
  })();

  const filteredAttendance = attendance;

  // Display controllers (live Protege list or seed)
  const displayControllers = isProtegeOrg ? liveControllers.map(c => ({
    id: c.id, name: c.name, site: 'City of Parramatta', ip: '—', doors: 0, readers: 0,
    status: c.online ? 'online' as const : 'offline' as const,
    firmware: '—', lastSeen: c.online ? 'Just now' : 'Unknown', health: c.online ? 'OK' : 'Offline',
  })) : filteredControllers;

  // Online count for the Controllers tile — derived from liveControllers (fast) not protegeInfo (slow)
  const controllersOnlineCount = isProtegeOrg
    ? liveControllers.filter(c => c.online).length
    : filteredControllers.filter(c => c.status === 'online').length;

  const controllersTotal = isProtegeOrg
    ? liveControllers.length
    : filteredControllers.length;

  const [tab, setTab] = useState<'events'|'doors'|'attendance'|'users'|'map'|'intercom'|'areas'>('doors');
  const [areaStates, setAreaStates] = useState<AreaSeed[]>(AREAS_SEED);
  const [mapFocusBanner, setMapFocusBanner] = useState<{ site:string; location:string; alarmType:string; severity:string; time:string } | null>(null);
  const [lockdownActive, setLockdownActive] = useState(false);
  const [lockdownLoading, setLockdownLoading] = useState(false);
  const [editMapLayout, setEditMapLayout] = useState(false);

  // Per-controller map helpers
  const mapKey = React.useCallback((suffix: string) => {
    return activeController ? `${suffix}_${activeController.id}` : suffix;
  }, [activeController]);

  const [doorPositions, setDoorPositions] = useState<Record<string,{x:number;y:number}>>(() => {
    try { return JSON.parse(localStorage.getItem('siteMapDoorPositions') || '{}'); } catch { return {}; }
  });
  const [mapBgImage, setMapBgImage] = useState<string>(() => localStorage.getItem('siteMapBgImage') || '');
  const [showMapZones, setShowMapZones] = useState<boolean>(() => localStorage.getItem('siteMapShowZones') !== 'false');
  const [cameraPositions, setCameraPositions] = useState<Record<string,{x:number;y:number}>>(() => {
    try { return JSON.parse(localStorage.getItem('siteMapCameraPositions') || '{}'); } catch { return {}; }
  });

  // Reload map state when active controller changes — try API first, fall back to localStorage
  React.useEffect(() => {
    const ctrlSuffix = activeController ? `_${activeController.id}` : '';
    // Load localStorage baseline immediately (no flicker)
    let localDoor: Record<string,{x:number;y:number}> = {};
    let localCam:  Record<string,{x:number;y:number}> = {};
    try { localDoor = JSON.parse(localStorage.getItem(`siteMapDoorPositions${ctrlSuffix}`) || '{}'); } catch {}
    try { localCam  = JSON.parse(localStorage.getItem(`siteMapCameraPositions${ctrlSuffix}`) || '{}'); } catch {}
    setDoorPositions(localDoor);
    setCameraPositions(localCam);
    setMapBgImage(localStorage.getItem(`siteMapBgImage${ctrlSuffix}`) || '');
    setShowMapZones(localStorage.getItem('siteMapShowZones') !== 'false');
    setEditMapLayout(false);
    setAcDrawMode('off');

    // Fetch from API and merge (API positions persist across browsers/devices)
    const token = localStorage.getItem('lemai_token') || localStorage.getItem('access_token') || '';
    if (token) {
      fetch('/api/sitemap/positions', { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (!data) return;
          const apiDoor: Record<string,{x:number;y:number}> = data.doorPositions || {};
          const apiCam:  Record<string,{x:number;y:number}> = data.cameraPositions || {};
          // localStorage takes priority for this controller (most recent local edits win)
          setDoorPositions(prev => ({ ...apiDoor, ...prev }));
          setCameraPositions(prev => ({ ...apiCam, ...prev }));
        })
        .catch(() => {});
    }
  }, [activeController?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper: save positions to API (org-wide persistence)
  const _saveMapToAPI = React.useCallback((doorPos: Record<string,any>, camPos: Record<string,any>) => {
    const token = localStorage.getItem('lemai_token') || localStorage.getItem('access_token') || '';
    if (!token) return;
    fetch('/api/sitemap/positions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ doorPositions: doorPos, cameraPositions: camPos }),
    }).catch(() => {});
  }, []);

  // Load saved drawing onto canvas when map tab becomes active or controller changes
  React.useEffect(() => {
    if (tab !== 'map') return;
    const canvas = acCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const drawKey = activeController ? `ac_drawing_${activeController.id}` : 'ac_drawing_global';
    const saved = localStorage.getItem(drawKey);
    if (saved) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      img.src = saved;
    }
  }, [tab, activeController?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const mapDropRef = useRef<HTMLDivElement>(null);
  const bgUploadRef = useRef<HTMLInputElement>(null);
  // Drawing overlay state for the map tab
  const [acDrawMode, setAcDrawMode] = useState<'off' | 'pencil' | 'eraser'>('off');
  const [acDrawColor, setAcDrawColor] = useState('#ef4444');
  const [acDrawSize, setAcDrawSize] = useState(4);
  const acCanvasRef = useRef<HTMLCanvasElement>(null);
  const acDrawingActive = useRef(false);
  const acLastPt = useRef<{ x: number; y: number } | null>(null);

  // Camera feed popup (appears when clicking a camera on the map)
  const [cameraFeedPopup, setCameraFeedPopup] = useState<{id:string;name:string;status:string}|null>(null);

  // Users tab: selected user for event expansion
  const [selectedUser, setSelectedUser] = useState<ProtegeCard|null>(null);
  const [userEvents,   setUserEvents]   = useState<ProtegeEvent[]>([]);
  const [userEventsLoading, setUserEventsLoading] = useState(false);

  // Events tab: date range + result filter
  const [eventDateFrom,    setEventDateFrom]    = useState('');
  const [eventDateTo,      setEventDateTo]      = useState('');
  const [eventResultFilter,setEventResultFilter]= useState<'all'|'granted'|'denied'>('all');
  const [eventsFetching,   setEventsFetching]   = useState(false);

  function fetchEvents(from = eventDateFrom, to = eventDateTo) {
    if (!isProtegeOrg) return;
    const token = getToken();
    if (!token) return;
    setEventsFetching(true);
    const params = new URLSearchParams({count:'200'});
    if (from) params.set('from', from);
    if (to)   params.set('to', to);
    apiGet(`/protege/events?${params}`, token)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.events) setProtegeEvents(d.events); })
      .catch(()=>{})
      .finally(() => setEventsFetching(false));
  }
  const { editing, setEditing, slots, isVisible: isTabVisible, orderedWidgets: orderedTabs, toggle: toggleTab, moveUp: moveTabUp, moveDown: moveTabDown, reset: resetTabs } = usePageCustomizer('access_v2', ACCESS_WIDGETS);
  const [search, setSearch] = useState('');
  const doors = filteredDoors;

  const [doorStates, setDoorStates] = useState<Record<number,Door>>(
    () => doors.reduce((a,d)=>({...a,[d.id]:d}),{} as Record<number,Door>)
  );

  // Sync doorStates when live doors load
  useEffect(() => {
    if (doors.length > 0) {
      setDoorStates(doors.reduce((a,d)=>({...a,[d.id]:d}),{} as Record<number,Door>));
    }
  }, [protegeDoors]);

  const { toast } = useToast();

  const controllersOnline = activeController ? (activeController.online ? 1 : 0) : controllersOnlineCount;
  const healthIssues = displayControllers.filter(c=>c.health!=='OK').length
    + doors.filter(d=>d.health!=='ok').length
    + doors.filter(d=>d.status==='forced-open'||d.status==='tamper').length;
  const staffIn = filteredAttendance.filter(a=>a.status==='in').length;
  const deniedToday = filteredEvents.filter(e=>e.result==='denied').length;

  function showNotif(msg:string) { toast(msg, 'info'); }

  function doorCommand(doorId:number, command:string) {
    const door = doorStates[doorId];
    let newStatus = door.status;
    let newMode   = door.mode;
    if (command==='Lock')         { newStatus='locked';   newMode='manual-lock'; }
    if (command==='Unlock')       { newStatus='unlocked'; newMode='manual-unlock'; }
    if (command==='Open')         { newStatus='open';     newMode='manual-unlock'; }
    if (command==='Force Lock')   { newStatus='locked';   newMode='forced'; }
    if (command==='Force Unlock') { newStatus='forced-open'; newMode='forced'; }
    if (command==='Clear Override') { newStatus='locked'; newMode='scheduled'; }
    setDoorStates(prev=>({...prev,[doorId]:{...prev[doorId],status:newStatus as Door['status'],mode:newMode as Door['mode'],lastEvent:`Manual: ${command}`,lastEventTime:'Now'}}));
    showNotif(`${command} sent to ${door.name}`);
    // Send to Protege GX if connected
    if (protegeStatus === 'connected') {
      const token = localStorage.getItem('lemai_token') || '';
      const cmdMap: Record<string,string> = { 'Lock':'lock','Unlock':'unlock','Open':'open','Force Lock':'lock','Force Unlock':'unlock','Clear Override':'cycle' };
      fetch(`/api/protege/door-command`, {
        method:'POST',
        headers:{ 'Authorization':`Bearer ${token}`, 'Content-Type':'application/json' },
        body: JSON.stringify({ doorIndex: doorId, command: cmdMap[command] || 'open', controllerId: 0, siteId: 0 }),
      }).catch(()=>{});
    }
  }

  const tabStyle = (t:string): React.CSSProperties => ({
    height:'32px', padding:'0 14px', border:'none',
    borderBottom:`2px solid ${tab===t?'#3b82f6':'transparent'}`,
    background:'transparent', cursor:'pointer',
    fontSize:'10px', fontWeight:700, letterSpacing:'0.06em',
    color:tab===t?'#e5e5e5':'#444',
    transition:'color 0.12s',
  });

  const tileStyle: React.CSSProperties = {
    backgroundColor:'#111111', border:'1px solid #1a1a1a',
    borderRadius:0, boxShadow:'none', padding:'14px', cursor:'pointer', transition:'all 0.15s',
  };

  return (
    <Layout title="Access Control" subtitle="Protege GX — Controller status, door commands and attendance">
      <style>{`
        @keyframes doorForcedFlash {
          0%,100% { border-color: #dc2626; }
          50%      { border-color: #111; }
        }
        @keyframes areaAlarmFlash {
          0%,100% { border-color: #dc2626; }
          50%      { border-color: #111; }
        }
      `}</style>
      {/* Camera feed popup — fixed overlay bottom-right */}
      {cameraFeedPopup && (
        <CameraFeedPopup
          id={cameraFeedPopup.id}
          name={cameraFeedPopup.name}
          status={cameraFeedPopup.status}
          onClose={() => setCameraFeedPopup(null)}
        />
      )}
      {editing && <CustomizerPanel widgets={ACCESS_WIDGETS} slots={slots} toggle={toggleTab} moveUp={moveTabUp} moveDown={moveTabDown} reset={resetTabs} onClose={() => setEditing(false)} />}

      {/* Protege GX connection status — only shown for Parramatta org */}
      {isProtegeOrg && protegeStatus === 'connected' ? (
        <div style={{background:'rgba(34,197,94,0.06)',border:'1px solid rgba(34,197,94,0.18)',borderRadius:'4px',padding:'9px 14px',marginBottom:'18px',display:'flex',alignItems:'center',gap:'8px'}}>
          <div style={{width:7,height:7,borderRadius:'50%',background:'#22c55e',boxShadow:'0 0 6px #22c55e',flexShrink:0}}/>
          <div style={{fontSize:'11px',color:'#a0a0a0',flex:1}}>
            <span style={{fontWeight:600,color:'#22c55e'}}>Protege GX Connected</span>
            {protegeVersion && <span style={{marginLeft:8,color:'#555555'}}>v{protegeVersion}</span>}
            {protegeInfo && (
              <span style={{marginLeft:12}}>
                {protegeInfo.siteCount} site{protegeInfo.siteCount !== 1 ? 's' : ''} · {protegeInfo.controllerCount ?? 0} controller{(protegeInfo.controllerCount ?? 0) !== 1 ? 's' : ''}
                {siteFilterName && <span style={{marginLeft:8,padding:'1px 7px',borderRadius:4,background:'rgba(99,102,241,0.1)',color:'#e5e5e5',fontSize:'10px'}}>Filtered: {siteFilterName}</span>}
                {!siteFilterName && protegeInfo.sites?.map((s: any) => (
                  <span key={s.id} style={{marginLeft:8,padding:'1px 7px',borderRadius:4,background:'rgba(34,197,94,0.08)',color:'#22c55e',fontSize:'10px'}}>{s.name}</span>
                ))}
              </span>
            )}
          </div>
          <div style={{fontSize:'10px',color:'#22c55e',background:'rgba(34,197,94,0.1)',padding:'2px 8px',borderRadius:6}}>LIVE</div>
        </div>
      ) : isProtegeOrg && protegeStatus === 'loading' ? (
        <div style={{background:'rgba(99,102,241,0.06)',border:'1px solid rgba(99,102,241,0.15)',borderRadius:'4px',padding:'9px 14px',marginBottom:'18px',display:'flex',alignItems:'center',gap:'8px'}}>
          <div className="skeleton" style={{width:7,height:7,borderRadius:'50%',flexShrink:0}}/>
          <div style={{fontSize:'11px',color:'#555555'}}>Connecting to Protege GX…</div>
        </div>
      ) : isProtegeOrg ? (
        <div style={{background:'rgba(245,158,11,0.07)',border:'1px solid rgba(245,158,11,0.18)',borderRadius:'4px',padding:'9px 14px',marginBottom:'18px',display:'flex',alignItems:'center',gap:'8px'}}>
          <span style={{fontSize:'13px'}}>⚠️</span>
          <div style={{fontSize:'11px',color:'#a0a0a0'}}>
            <span style={{fontWeight:600,color:'#f59e0b'}}>Protege GX Offline</span> — Cannot reach controller. Check VPN connection to 172.26.16.250.
          </div>
        </div>
      ) : null}

      {/* ── Page Header ── */}
      <div style={{ background:'#0d0d0d', borderBottom:'1px solid #1a1a1a', padding:'0 0 0 0', marginBottom:'14px', display:'flex', alignItems:'center', height:'36px' }}>
        <span style={{ fontSize:'11px', fontWeight:700, color:'#e5e5e5', textTransform:'uppercase', letterSpacing:'0.08em', flex:1 }}>ACCESS CONTROL</span>
        <div style={{ display:'flex', gap:'6px', alignItems:'center' }}>
          <button
            disabled={lockdownLoading}
            onClick={async () => {
              if (lockdownActive) {
                if (!window.confirm('Release lockdown? This will unlock all doors to their scheduled mode.')) return;
                setLockdownLoading(true);
                const tok = localStorage.getItem('lemai_token') || '';
                try {
                  await fetch('/api/protege/lockdown', { method:'POST', headers:{ Authorization:`Bearer ${tok}`, 'Content-Type':'application/json' }, body: JSON.stringify({ action:'unlock' }) });
                  setLockdownActive(false);
                  toast('Lockdown RELEASED — all doors returning to scheduled mode', 'success');
                } catch { toast('Release command sent — verify in Protege GX', 'warning'); }
                setLockdownLoading(false);
              } else {
                if (!window.confirm('EMERGENCY LOCKDOWN — This will LOCK ALL doors immediately. Confirm?')) return;
                setLockdownLoading(true);
                const tok = localStorage.getItem('lemai_token') || '';
                try {
                  await fetch('/api/protege/lockdown', { method:'POST', headers:{ Authorization:`Bearer ${tok}`, 'Content-Type':'application/json' }, body: JSON.stringify({ action:'lock' }) });
                  setLockdownActive(true);
                  toast('EMERGENCY LOCKDOWN ACTIVE — all doors locked', 'error');
                } catch { toast('Lockdown command sent — verify in Protege GX', 'warning'); }
                setLockdownLoading(false);
              }
            }}
            style={{
              height:'26px', padding:'0 10px', cursor: lockdownLoading ? 'wait' : 'pointer',
              border:'1px solid #dc2626', background:'#450a0a', color:'#dc2626',
              fontSize:'10px', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em',
              borderRadius:0,
            }}
          >
            {lockdownLoading && !lockdownActive ? '...' : 'LOCK ALL'}
          </button>
          <button
            disabled={lockdownLoading}
            onClick={async () => {
              if (!window.confirm('Release lockdown? This will unlock all doors to their scheduled mode.')) return;
              setLockdownLoading(true);
              const tok = localStorage.getItem('lemai_token') || '';
              try {
                await fetch('/api/protege/lockdown', { method:'POST', headers:{ Authorization:`Bearer ${tok}`, 'Content-Type':'application/json' }, body: JSON.stringify({ action:'unlock' }) });
                setLockdownActive(false);
                toast('All doors returned to scheduled mode', 'success');
              } catch { toast('Unlock command sent — verify in Protege GX', 'warning'); }
              setLockdownLoading(false);
            }}
            style={{
              height:'26px', padding:'0 10px', cursor: lockdownLoading ? 'wait' : 'pointer',
              border:'1px solid #16a34a', background:'#052e16', color:'#16a34a',
              fontSize:'10px', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em',
              borderRadius:0,
            }}
          >
            {lockdownLoading && lockdownActive ? '...' : 'UNLOCK ALL'}
          </button>
        </div>
      </div>

      {/* ── 4 Interactive Tiles ── */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'12px',marginBottom:'20px'}}>
        {/* Controllers Online */}
        <div style={{...tileStyle, borderColor: activeController ? 'rgba(99,102,241,0.25)' : 'rgba(255,255,255,0.06)'}}
          onClick={()=>{ if(activeController){ setActiveController(null); } else { setShowControllerList(true); } }}
          onMouseOver={e=>{(e.currentTarget as HTMLDivElement).style.background='#1e3a5f';(e.currentTarget as HTMLDivElement).style.borderColor='rgba(59,130,246,0.2)';}}
          onMouseOut={e=>{(e.currentTarget as HTMLDivElement).style.background='#111111';(e.currentTarget as HTMLDivElement).style.borderColor=activeController?'rgba(99,102,241,0.25)':'rgba(255,255,255,0.06)';}}
        >
          <div style={{display:'flex',justifyContent:'space-between',marginBottom:'10px'}}>
            <span style={{fontSize:'9px',color:'#555555',textTransform:'uppercase',letterSpacing:'0.5px'}}>Controllers Online</span>
            <span style={{fontSize:'16px'}}>🖥️</span>
          </div>
          <div style={{fontSize:'22px',fontWeight:700,color:'#22c55e'}}>
            {controllersOnline}
            <span style={{fontSize:'16px',color:'#555555'}}>/{controllersTotal}</span>
          </div>
          <div style={{fontSize:'10px',color:'#444',marginTop:'3px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
            {activeController ? <span style={{color:'#e5e5e5'}}>↩ Tap to clear: {activeController.name.split('-').slice(0,3).join('-')}</span> : 'Click to view all controllers'}
          </div>
          <div style={{fontSize:'9px',color:'#3b82f6',marginTop:'6px'}}>{activeController ? '← Back to all controllers' : 'Click to browse controllers →'}</div>
        </div>

        {/* Users */}
        <div style={tileStyle}
          onClick={()=>{setTab('users');}}
          onMouseOver={e=>{(e.currentTarget as HTMLDivElement).style.background='rgba(168,85,247,0.05)';(e.currentTarget as HTMLDivElement).style.borderColor='rgba(168,85,247,0.2)';}}
          onMouseOut={e=>{(e.currentTarget as HTMLDivElement).style.background='#111111';(e.currentTarget as HTMLDivElement).style.borderColor='#1a1a1a';}}
        >
          <div style={{display:'flex',justifyContent:'space-between',marginBottom:'10px'}}>
            <span style={{fontSize:'9px',color:'#555555',textTransform:'uppercase',letterSpacing:'0.5px'}}>Users</span>
            <span style={{fontSize:'16px'}}>🪪</span>
          </div>
          <div style={{fontSize:'22px',fontWeight:700,color:'#a855f7'}}>
            {isProtegeOrg
              ? protegeCardsLoading
                ? <span style={{fontSize:'14px',color:'#555555'}}>Loading…</span>
                : protegeCards.length > 0
                  ? protegeCards.length
                  : '—'
              : isDemoOrg ? attendance.length : '—'}
          </div>
          <div style={{fontSize:'10px',color:'#444',marginTop:'3px'}}>Registered users</div>
          <div style={{fontSize:'9px',color:'#3b82f6',marginTop:'6px'}}>Click to view users →</div>
        </div>

        {/* Access Levels */}
        <div style={{...tileStyle, borderColor: showAccessLevels ? 'rgba(59,130,246,0.3)' : 'rgba(255,255,255,0.06)'}}
          onClick={()=>setShowAccessLevels(p=>!p)}
          onMouseOver={e=>{(e.currentTarget as HTMLDivElement).style.background='rgba(59,130,246,0.05)';(e.currentTarget as HTMLDivElement).style.borderColor='rgba(59,130,246,0.2)';}}
          onMouseOut={e=>{(e.currentTarget as HTMLDivElement).style.background='#111111';(e.currentTarget as HTMLDivElement).style.borderColor=showAccessLevels?'rgba(59,130,246,0.3)':'rgba(255,255,255,0.06)';}}
        >
          <div style={{display:'flex',justifyContent:'space-between',marginBottom:'10px'}}>
            <span style={{fontSize:'9px',color:'#555555',textTransform:'uppercase',letterSpacing:'0.5px'}}>Access Levels</span>
            <span style={{fontSize:'16px'}}>🔑</span>
          </div>
          <div style={{fontSize:'22px',fontWeight:700,color:'#3b82f6'}}>
            {isProtegeOrg
              ? protegeAlLoading
                ? <span style={{fontSize:'14px',color:'#555555'}}>Loading…</span>
                : protegeAccessLevels.length > 0
                  ? protegeAccessLevels.length
                  : '—'
              : isDemoOrg ? '12' : '-'}
          </div>
          <div style={{fontSize:'10px',color:'#444',marginTop:'3px'}}>Permission groups</div>
          <div style={{fontSize:'9px',color:'#3b82f6',marginTop:'6px'}}>{showAccessLevels ? 'Click to close' : 'Click to view ->'}</div>
        </div>

        {/* Schedules */}
        <div style={tileStyle}
          onClick={()=>setTab('events')}
          onMouseOver={e=>{(e.currentTarget as HTMLDivElement).style.background='rgba(245,158,11,0.05)';(e.currentTarget as HTMLDivElement).style.borderColor='rgba(245,158,11,0.2)';}}
          onMouseOut={e=>{(e.currentTarget as HTMLDivElement).style.background='#111111';(e.currentTarget as HTMLDivElement).style.borderColor='#1a1a1a';}}
        >
          <div style={{display:'flex',justifyContent:'space-between',marginBottom:'10px'}}>
            <span style={{fontSize:'9px',color:'#555555',textTransform:'uppercase',letterSpacing:'0.5px'}}>Schedules</span>
            <span style={{fontSize:'16px'}}>🕐</span>
          </div>
          <div style={{fontSize:'22px',fontWeight:700,color:'#f59e0b'}}>
            {isProtegeOrg ? (protegeInfo?.scheduleCount ?? '—') : isDemoOrg ? '8' : '—'}
          </div>
          <div style={{fontSize:'10px',color:'#444',marginTop:'3px'}}>Time zones / access schedules</div>
          <div style={{fontSize:'9px',color:'#3b82f6',marginTop:'6px'}}>View events →</div>
        </div>
      </div>

      {/* ── Active controller breadcrumb ── */}
      {activeController && (
        <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'12px',padding:'8px 14px',background:'#111111',border:'1px solid #333333',borderRadius:'4px'}}>
          <span style={{fontSize:'11px',color:'#555555'}}>Filtered:</span>
          <span style={{fontSize:'12px',color:'#e5e5e5',fontWeight:600}}>{activeController.name}</span>
          <div style={{width:6,height:6,borderRadius:'50%',background:activeController.online?'#22c55e':'#ef4444',boxShadow:`0 0 4px ${activeController.online?'#22c55e':'#ef4444'}`}}/>
          <span style={{fontSize:'10px',color:activeController.online?'#22c55e':'#ef4444'}}>{activeController.online?'Online':'Offline'}</span>
          <div style={{flex:1}}/>
          <button onClick={()=>setActiveController(null)} style={{padding:'3px 10px',background:'#1e3a5f',border:'1px solid #3b82f6',borderRadius:'6px',color:'#e5e5e5',fontSize:'11px',cursor:'pointer'}}>✕ Clear filter</button>
        </div>
      )}

      {/* ── Access Levels panel ── */}
      {showAccessLevels && (
        <div style={{background:'#111',border:'1px solid rgba(59,130,246,0.15)',borderRadius:'5px',marginBottom:'16px',overflow:'hidden'}}>
          <div style={{padding:'12px 16px',borderBottom:'1px solid #1a1a1a',display:'flex',alignItems:'center',gap:'10px'}}>
            <div style={{flex:1,minWidth:0}}>
              <span style={{fontSize:'13px',fontWeight:700,color:'#e5e5e5'}}>Access Levels</span>
              <span style={{fontSize:'11px',color:'#555555',marginLeft:10}}>
                {isProtegeOrg ? (protegeAccessLevels.length > 0 ? `${protegeAccessLevels.length} groups` : 'Loading…') : '12 groups (demo)'}
              </span>
            </div>
            <input
              value={alSearch} onChange={e=>setAlSearch(e.target.value)}
              placeholder="Search levels..."
              style={{padding:'5px 10px',borderRadius:'7px',border:'1px solid rgba(255,255,255,0.1)',background:'rgba(255,255,255,0.05)',color:'#e5e5e5',fontSize:'11px',outline:'none',width:'160px'}}
            />
            <button onClick={()=>{setShowAccessLevels(false);setSelectedAl(null);setAlUsers([]);}} style={{background:'none',border:'none',color:'#555555',cursor:'pointer',fontSize:'16px',lineHeight:1}}>✕</button>
          </div>
          <div style={{display:'flex',maxHeight:'380px'}}>
            {/* Left: access level list */}
            <div style={{flex:1,overflowY:'auto',borderRight: selectedAl ? '1px solid rgba(255,255,255,0.06)' : 'none'}}>
              {isProtegeOrg && protegeAccessLevels.length === 0 && (
                <div style={{padding:'24px',textAlign:'center',color:'#555555',fontSize:'12px'}}>Loading access levels…</div>
              )}
              {(isProtegeOrg ? protegeAccessLevels : [
                {id:'1',name:'All Access 24/7'},{id:'2',name:'Business Hours'},{id:'3',name:'After Hours Staff'},
                {id:'4',name:'Security Personnel'},{id:'5',name:'Visitor Escorted'},{id:'6',name:'Contractor Day Only'},
                {id:'7',name:'Executive Suite'},{id:'8',name:'IT Server Room'},{id:'9',name:'Car Park Only'},
                {id:'10',name:'Fire Warden'},{id:'11',name:'Cleaner After Hours'},{id:'12',name:'Emergency Access'},
              ]).filter(al => !alSearch || al.name.toLowerCase().includes(alSearch.toLowerCase()) || al.id.includes(alSearch))
               .map((al, i) => {
                const isSelected = selectedAl?.id === al.id;
                return (
                  <div key={al.id}
                    onClick={()=>{
                      if (isSelected) { setSelectedAl(null); setAlUsers([]); return; }
                      setSelectedAl(al);
                      setAlUsers([]);
                      if (isProtegeOrg) {
                        setAlUsersLoading(true);
                        const tok = localStorage.getItem('lemai_token')||'';
                        fetch(`/api/protege/access-levels/${al.id}/users`, {headers:{Authorization:`Bearer ${tok}`}})
                          .then(r=>r.ok?r.json():null)
                          .then(d=>{ setAlUsers(d?.users||[]); setAlUsersLoading(false); })
                          .catch(()=>setAlUsersLoading(false));
                      } else {
                        // Demo users per access level
                        const demoMap: Record<string,{id:string;name:string;name2:string;department:string}[]> = {
                          '1':[{id:'u1',name:'Wei Zhang',name2:'',department:'IT'},{id:'u2',name:'Sarah Mitchell',name2:'',department:'Security'},{id:'u3',name:'John Okafor',name2:'',department:'Operations'}],
                          '2':[{id:'u4',name:'Marcus Torres',name2:'',department:'Admin'},{id:'u5',name:'Lisa Chen',name2:'',department:'Finance'},{id:'u6',name:'David Park',name2:'',department:'HR'},{id:'u7',name:'Emma Wilson',name2:'',department:'Customer Service'}],
                          '3':[{id:'u8',name:'James Patel',name2:'',department:'Maintenance'},{id:'u9',name:'Rachel Kim',name2:'',department:'Security'}],
                          '4':[{id:'u2',name:'Sarah Mitchell',name2:'',department:'Security'},{id:'u10',name:'Officer Brown',name2:'',department:'Security'}],
                        };
                        setAlUsers(demoMap[al.id] || [{id:'u99',name:'No users assigned',name2:'',department:''}]);
                      }
                    }}
                    style={{display:'flex',alignItems:'center',gap:'12px',padding:'10px 16px',borderBottom:'1px solid rgba(255,255,255,0.03)',cursor:'pointer',
                      background:isSelected?'rgba(59,130,246,0.1)':i%2===0?'transparent':'rgba(255,255,255,0.01)',transition:'background 0.12s'}}
                    onMouseOver={e=>{ if(!isSelected)(e.currentTarget as HTMLElement).style.background='#111'; }}
                    onMouseOut={e=>{ (e.currentTarget as HTMLElement).style.background=isSelected?'rgba(59,130,246,0.1)':i%2===0?'transparent':'rgba(255,255,255,0.01)'; }}
                  >
                    <div style={{width:28,height:28,borderRadius:'4px',background:isSelected?'rgba(59,130,246,0.2)':'rgba(59,130,246,0.1)',border:`1px solid ${isSelected?'rgba(59,130,246,0.4)':'rgba(59,130,246,0.2)'}`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                      <span style={{fontSize:'12px'}}>🔑</span>
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:'12px',color:isSelected?'#93c5fd':'#e2e8f0',fontWeight:isSelected?700:500}}>{al.name}</div>
                      <div style={{fontSize:'10px',color:'#555555'}}>ID: {al.id}</div>
                    </div>
                    <div style={{fontSize:'10px',color:'#3b82f6',flexShrink:0}}>{isSelected?'▶':''}</div>
                  </div>
                );
              })}
            </div>
            {/* Right: users for selected access level */}
            {selectedAl && (
              <div style={{width:'260px',flexShrink:0,overflowY:'auto'}}>
                <div style={{padding:'10px 14px',borderBottom:'1px solid #1a1a1a',background:'rgba(59,130,246,0.04)'}}>
                  <div style={{fontSize:'11px',fontWeight:700,color:'#3b82f6'}}>{selectedAl.name}</div>
                  <div style={{fontSize:'10px',color:'#555555',marginTop:'2px'}}>Assigned users</div>
                </div>
                {alUsersLoading ? (
                  <div style={{padding:'20px',textAlign:'center',color:'#555555',fontSize:'11px'}}>Loading users…</div>
                ) : alUsers.length === 0 ? (
                  <div style={{padding:'20px',textAlign:'center',color:'#555555',fontSize:'11px'}}>No users assigned to this access level</div>
                ) : (
                  alUsers.map(u => (
                    <div key={u.id} style={{display:'flex',alignItems:'center',gap:'10px',padding:'9px 14px',borderBottom:'1px solid rgba(255,255,255,0.03)'}}>
                      <div style={{width:28,height:28,borderRadius:'50%',background:'rgba(99,102,241,0.15)',border:'1px solid rgba(99,102,241,0.25)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,fontSize:'11px',fontWeight:700,color:'#818cf8'}}>
                        {(u.name||'?')[0]}
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:'11px',color:'#e5e5e5',fontWeight:500,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                          {u.name}{u.name2 ? ` ${u.name2}` : ''}
                        </div>
                        {u.department && <div style={{fontSize:'9px',color:'#555555',marginTop:'1px'}}>{u.department}</div>}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Controller list panel (drill-down) ── */}
      {showControllerList && (
        <div style={{background:'#111',border:'1px solid #1a1a1a',borderRadius:'5px',marginBottom:'16px',overflow:'hidden'}}>
          <div style={{padding:'12px 16px',borderBottom:'1px solid #1a1a1a',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
            <div>
              <span style={{fontSize:'13px',fontWeight:700,color:'#e5e5e5'}}>Controllers</span>
              <span style={{fontSize:'11px',color:'#555555',marginLeft:10}}>
                {isProtegeOrg ? `${controllersOnlineCount} of ${controllersTotal} online` : `${displayControllers.filter(c=>c.status==='online').length} of ${displayControllers.length} online`}
              </span>
            </div>
            <button onClick={()=>setShowControllerList(false)} style={{background:'none',border:'none',color:'#555555',cursor:'pointer',fontSize:'16px',lineHeight:1}}>✕</button>
          </div>
          <div style={{maxHeight:'320px',overflowY:'auto'}}>
            {(isProtegeOrg ? liveControllers : displayControllers.map(c=>({id:c.id,name:c.name,online:c.status==='online',siteId:''}))).map((c,i) => {
              const isActive = activeController?.id === c.id;
              return (
                <div key={c.id || i}
                  onClick={()=>{setActiveController({id:c.id,name:c.name,online:c.online});setShowControllerList(false);setTab('map');}}
                  style={{display:'flex',alignItems:'center',gap:'10px',padding:'10px 16px',borderBottom:'1px solid rgba(255,255,255,0.03)',cursor:'pointer',background:isActive?'#1e3a5f':'transparent',transition:'background 0.15s'}}
                  onMouseOver={e=>(e.currentTarget as HTMLDivElement).style.background='#111111'}
                  onMouseOut={e=>(e.currentTarget as HTMLDivElement).style.background=isActive?'rgba(99,102,241,0.08)':'transparent'}
                >
                  <div style={{width:8,height:8,borderRadius:'50%',background:c.online?'#22c55e':'#ef4444',boxShadow:`0 0 5px ${c.online?'#22c55e':'#ef4444'}80`,flexShrink:0}}/>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:'12px',color:'#e5e5e5',fontWeight:500,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.name}</div>
                    <div style={{fontSize:'10px',color:'#555555'}}>ID: {c.id}</div>
                  </div>
                  <div style={{fontSize:'10px',fontWeight:600,color:c.online?'#22c55e':'#ef4444'}}>{c.online?'ONLINE':'OFFLINE'}</div>
                  <div style={{fontSize:'10px',color:'#3b82f6'}}>→</div>
                </div>
              );
            })}
            {(isProtegeOrg ? liveControllers : displayControllers).length === 0 && (
              <div style={{padding:'24px',textAlign:'center',color:'#555555',fontSize:'12px'}}>No controllers found</div>
            )}
          </div>
        </div>
      )}

      {/* ── Main card ── */}
      <div style={{background:'#0d0d0d',border:'1px solid #1a1a1a',borderRadius:0,overflow:'hidden'}}>
        <div style={{borderBottom:'1px solid #1a1a1a',display:'flex',alignItems:'center',justifyContent:'space-between',background:'#050505'}}>
          <div style={{display:'flex',alignItems:'center'}}>
            {(['doors','areas','events','users'] as const).map(t => {
              const labels: Record<string,string> = { doors:'DOORS', areas:'AREAS', events:'ACCESS EVENTS', users:'CARDHOLDERS' };
              return <button key={t} style={tabStyle(t)} onClick={()=>setTab(t)}>{labels[t]}</button>;
            })}
            {orderedTabs.filter(w => isTabVisible(w.id) && !['doors','events','users'].includes(w.id)).map(w => {
              const t = w.id as 'attendance'|'map'|'intercom';
              const labels: Record<string,string> = { attendance:'ATTENDANCE', map:'MAP', intercom:'INTERCOM' };
              if (!labels[t]) return null;
              return <button key={t} style={tabStyle(t)} onClick={()=>setTab(t)}>{labels[t]}</button>;
            })}
            <CustomizeButton editing={editing} setEditing={setEditing} />
          </div>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search..."
            style={{background:'#111',border:'1px solid #333',borderRadius:2,padding:'0 8px',height:'26px',color:'#e5e5e5',fontSize:'10px',width:'160px',outline:'none',margin:'4px 8px'}}
          />
        </div>

        {/* ── AREAS Tab ── */}
        {tab==='areas' && (
          <div>
            {!isProtegeOrg ? (
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:0}}>
                {areaStates.map(area => {
                  const isArmed   = area.status === 'armed';
                  const isAlarmed = area.status === 'alarmed';
                  const dot = isAlarmed ? '#dc2626' : isArmed ? '#16a34a' : '#555';
                  const label = isAlarmed ? 'ALARM' : isArmed ? 'ARMED' : 'DISARMED';
                  return (
                    <div key={area.id} style={{
                      background:'#111', border:'1px solid #222', borderRadius:0, padding:'8px',
                      animation: isAlarmed ? 'areaAlarmFlash 500ms step-end infinite' : 'none',
                    }}>
                      <div style={{fontSize:'11px',fontWeight:700,color:'#e5e5e5',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',marginBottom:'4px'}}>{area.name}</div>
                      <div style={{display:'flex',alignItems:'center',gap:'4px',marginBottom:'4px'}}>
                        <div style={{width:8,height:8,borderRadius:'50%',background:dot,flexShrink:0}}/>
                        <span style={{fontSize:'9px',fontWeight:700,color:dot,textTransform:'uppercase',letterSpacing:'0.06em'}}>{label}</span>
                      </div>
                      <div style={{fontSize:'9px',color:'#555',marginBottom:'6px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                        {area.lastBy} · {area.lastAt}
                      </div>
                      <button
                        onClick={()=>setAreaStates(prev=>prev.map(a=>a.id===area.id?{...a,status:a.status==='armed'?'disarmed':'armed',lastBy:'Operator',lastAt:new Date().toLocaleTimeString('en-AU',{hour:'2-digit',minute:'2-digit',hour12:false})}:a))}
                        style={{
                          width:'100%',height:'24px',background:'#0a0a0a',
                          border:'1px solid #333',borderRadius:0,
                          color:'#888',fontSize:'9px',fontWeight:700,
                          textTransform:'uppercase',letterSpacing:'0.05em',cursor:'pointer',
                        }}
                        onMouseEnter={e=>{(e.currentTarget as HTMLButtonElement).style.background='#1e3a5f';(e.currentTarget as HTMLButtonElement).style.color='#3b82f6';}}
                        onMouseLeave={e=>{(e.currentTarget as HTMLButtonElement).style.background='#0a0a0a';(e.currentTarget as HTMLButtonElement).style.color='#888';}}
                      >
                        {isArmed ? 'DISARM' : 'ARM'}
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{padding:'40px',textAlign:'center',color:'#444',fontSize:'11px'}}>
                Protege GX area data not available in this version
              </div>
            )}
          </div>
        )}

        {/* ── Events Tab ── */}
        {tab==='events' && (() => {
          const evBase = filteredEvents
            .filter(e => eventResultFilter === 'all' || e.result === eventResultFilter)
            .filter(e => search === '' || e.user.toLowerCase().includes(search.toLowerCase()) || e.door.toLowerCase().includes(search.toLowerCase()) || e.result.includes(search.toLowerCase()));
          return (
            <>
              {/* Filter toolbar */}
              <div style={{padding:'10px 16px',borderBottom:'1px solid #1a1a1a',display:'flex',gap:'8px',flexWrap:'wrap',alignItems:'center'}}>
                <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
                  <span style={{fontSize:'10px',color:'#555555',whiteSpace:'nowrap'}}>From:</span>
                  <input type="date" value={eventDateFrom} onChange={e=>setEventDateFrom(e.target.value)}
                    style={{background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'6px',padding:'4px 8px',color:'#e5e5e5',fontSize:'11px',colorScheme:'dark'}}/>
                </div>
                <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
                  <span style={{fontSize:'10px',color:'#555555',whiteSpace:'nowrap'}}>To:</span>
                  <input type="date" value={eventDateTo} onChange={e=>setEventDateTo(e.target.value)}
                    style={{background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'6px',padding:'4px 8px',color:'#e5e5e5',fontSize:'11px',colorScheme:'dark'}}/>
                </div>
                <div style={{display:'flex',gap:'4px'}}>
                  {(['all','granted','denied'] as const).map(f=>(
                    <button key={f} onClick={()=>setEventResultFilter(f)}
                      style={{padding:'4px 10px',borderRadius:'6px',fontSize:'10px',fontWeight:600,cursor:'pointer',
                        background:eventResultFilter===f?(f==='granted'?'rgba(34,197,94,0.2)':f==='denied'?'rgba(239,68,68,0.2)':'rgba(99,102,241,0.2)'):'rgba(255,255,255,0.04)',
                        border:eventResultFilter===f?(f==='granted'?'1px solid rgba(34,197,94,0.4)':f==='denied'?'1px solid rgba(239,68,68,0.4)':'1px solid rgba(99,102,241,0.4)'):'1px solid rgba(255,255,255,0.08)',
                        color:eventResultFilter===f?(f==='granted'?'#22c55e':f==='denied'?'#ef4444':'#a5b4fc'):'#555555',
                      }}
                    >{f.charAt(0).toUpperCase()+f.slice(1)}</button>
                  ))}
                </div>
                <button onClick={() => fetchEvents()}
                  disabled={eventsFetching}
                  style={{padding:'4px 14px',borderRadius:'6px',fontSize:'11px',fontWeight:700,cursor:'pointer',
                    background:'rgba(59,130,246,0.15)',border:'1px solid rgba(59,130,246,0.35)',color:'#3b82f6'}}
                >{eventsFetching ? 'Loading…' : '🔍 Search events'}</button>
                {(eventDateFrom||eventDateTo) && (
                  <button onClick={()=>{setEventDateFrom('');setEventDateTo('');fetchEvents('','');}}
                    style={{padding:'4px 8px',borderRadius:'6px',fontSize:'10px',cursor:'pointer',background:'#111111',border:'1px solid #1a1a1a',color:'#555555'}}>Clear dates</button>
                )}
                <div style={{flex:1}}/>
                <span style={{fontSize:'10px',color:'#444444'}}>{evBase.length} event{evBase.length!==1?'s':''}</span>
                <button onClick={()=>exportCSV(`access-events-${csvDateStamp()}`,['User','Door','Time','Result'],evBase.map(e=>[e.user,e.door,e.time,e.result]))}
                  style={{padding:'4px 10px',background:'rgba(34,197,94,0.1)',border:'1px solid rgba(34,197,94,0.25)',borderRadius:'6px',color:'#22c55e',fontSize:'10px',fontWeight:600,cursor:'pointer'}}>↓ CSV</button>
              </div>

              {eventsFetching ? (
                <div style={{padding:'40px',textAlign:'center',color:'#555555',fontSize:'12px'}}>Searching events…</div>
              ) : evBase.length === 0 ? (
                <div style={{padding:'20px 16px'}}>
                  <EmptyState icon="🔑"
                    title={filteredEvents.length === 0 ? 'No events found' : 'No events match filters'}
                    description={isProtegeOrg
                      ? 'Try selecting a date range and clicking Search Events. Events are polled every 15 seconds when the system is active.'
                      : 'Access events will appear here once your system is integrated.'}
                    size="sm"
                  />
                  {isProtegeOrg && (
                    <div style={{marginTop:'12px',display:'flex',gap:'8px',justifyContent:'center',flexWrap:'wrap'}}>
                      {[
                        {label:'Today', from:new Date().toISOString().slice(0,10), to:''},
                        {label:'Last 7 days', from:new Date(Date.now()-7*86400000).toISOString().slice(0,10), to:''},
                        {label:'This month', from:new Date(new Date().getFullYear(),new Date().getMonth(),1).toISOString().slice(0,10), to:''},
                      ].map(p=>(
                        <button key={p.label} onClick={()=>{setEventDateFrom(p.from);setEventDateTo(p.to);fetchEvents(p.from,p.to);}}
                          style={{padding:'5px 14px',borderRadius:'7px',fontSize:'11px',fontWeight:600,cursor:'pointer',background:'rgba(59,130,246,0.12)',border:'1px solid rgba(59,130,246,0.25)',color:'#3b82f6'}}>
                          {p.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:'11px'}}>
                <thead>
                  <tr style={{background:'#0d0d0d',borderBottom:'1px solid #222'}}>
                    {['TIME','DOOR','CARDHOLDER','CREDENTIAL','EVENT TYPE','AREA','ACTIONS'].map(h=>(
                      <th key={h} style={{padding:'6px 10px',textAlign:'left',fontSize:'9px',fontWeight:700,color:'#444',textTransform:'uppercase',letterSpacing:'0.06em',whiteSpace:'nowrap'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {evBase.map((e,i)=>{
                    const isForced = ((e as any).eventDesc || (e as any).eventType || '').toLowerCase().includes('force');
                    const isDenied = e.result === 'denied';
                    const isHeld   = ((e as any).eventDesc || (e as any).eventType || '').toLowerCase().includes('held');
                    const badgeBg  = isForced ? '#450a0a' : isDenied ? '#450a0a' : isHeld ? '#2d1f00' : e.result === 'granted' ? (['entry','Entry'].some(x=>((e as any).eventDesc||'').includes(x)) ? '#052e16' : '#172554') : '#1a1a1a';
                    const badgeTx  = isForced ? '#fca5a5' : isDenied ? '#fca5a5' : isHeld ? '#fde047' : e.result === 'granted' ? (['entry','Entry'].some(x=>((e as any).eventDesc||'').includes(x)) ? '#86efac' : '#93c5fd') : '#555';
                    const badgeLabel = isForced ? 'FORCED' : isDenied ? 'DENIED' : isHeld ? 'DOOR HELD' : e.result === 'granted' ? (['exit','Exit'].some(x=>((e as any).eventDesc||'').includes(x)) ? 'VALID EXIT' : 'VALID ENTRY') : (e.result||'—').toUpperCase();
                    return (
                      <tr key={e.id||i}
                        style={{borderBottom:'1px solid #1a1a1a',background:isForced?'rgba(69,10,10,0.4)':'transparent'}}
                        onMouseOver={ev=>(ev.currentTarget.style.background=isForced?'rgba(69,10,10,0.5)':'#141414')}
                        onMouseOut={ev=>(ev.currentTarget.style.background=isForced?'rgba(69,10,10,0.4)':'transparent')}
                      >
                        <td style={{padding:'5px 10px',fontSize:'10px',color:'#888',fontFamily:'monospace',whiteSpace:'nowrap'}}>{e.time}</td>
                        <td style={{padding:'5px 10px',fontSize:'11px',color:'#e5e5e5',whiteSpace:'nowrap',maxWidth:'140px',overflow:'hidden',textOverflow:'ellipsis'}}>{e.door}</td>
                        <td style={{padding:'5px 10px',fontSize:'11px',color:'#e5e5e5',whiteSpace:'nowrap'}}>{e.user || 'Unknown'}</td>
                        <td style={{padding:'5px 10px',fontSize:'10px',color:'#a0a0a0',fontFamily:'monospace'}}>{e.card || '—'}</td>
                        <td style={{padding:'5px 10px'}}>
                          <span style={{padding:'1px 6px',fontSize:'9px',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.04em',background:badgeBg,color:badgeTx,whiteSpace:'nowrap'}}>{badgeLabel}</span>
                        </td>
                        <td style={{padding:'5px 10px',fontSize:'10px',color:'#555',whiteSpace:'nowrap'}}>{e.site || '—'}</td>
                        <td style={{padding:'5px 10px'}}>
                          <button style={{width:18,height:18,background:'#1a1a1a',border:'1px solid #333',borderRadius:0,color:'#555',cursor:'pointer',fontSize:'9px',display:'flex',alignItems:'center',justifyContent:'center'}}
                            onClick={()=>showNotif(`Event: ${e.user} · ${e.door} · ${e.time}`)}>↗</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              )}
            </>
          );
        })()}

        {/* ── DOORS Tab ── */}
        {tab==='doors' && (
          <div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:0}}>
              {Object.values(doorStates)
                .filter(d => !activeController || (d as any).controllerId === activeController.id || d.controller === activeController.name)
                .filter(d=>search===''||d.name.toLowerCase().includes(search.toLowerCase())||d.site.toLowerCase().includes(search.toLowerCase())||d.controller.toLowerCase().includes(search.toLowerCase()))
                .map(door => {
                  const isForced = door.status === 'forced-open' || door.status === 'tamper';
                  const statusDot = door.status === 'locked' ? '#16a34a'
                    : door.status === 'unlocked' ? '#f59e0b'
                    : door.status === 'forced-open' ? '#dc2626'
                    : door.status === 'open' ? '#eab308'
                    : '#6b7280';
                  const statusLabel = door.status === 'locked' ? 'LOCKED'
                    : door.status === 'unlocked' ? 'UNLOCKED'
                    : door.status === 'forced-open' ? 'FORCED'
                    : door.status === 'open' ? 'OPEN'
                    : door.status === 'tamper' ? 'TAMPER'
                    : (door.status as string).toUpperCase();
                  const isLocked = door.status === 'locked' || door.mode === 'manual-lock';
                  return (
                    <div key={door.id} style={{
                      background:'#111',
                      border:'1px solid #222',
                      borderRadius:0,
                      padding:'8px',
                      animation: isForced ? 'doorForcedFlash 500ms step-end infinite' : 'none',
                    }}>
                      <div style={{fontSize:'11px',fontWeight:700,color:'#e5e5e5',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',marginBottom:'4px'}}>{door.name}</div>
                      <div style={{display:'flex',alignItems:'center',gap:'4px',marginBottom:'4px'}}>
                        <div style={{width:8,height:8,borderRadius:'50%',background:statusDot,flexShrink:0}}/>
                        <span style={{fontSize:'9px',fontWeight:700,color:statusDot,textTransform:'uppercase',letterSpacing:'0.06em'}}>{statusLabel}</span>
                      </div>
                      <div style={{fontSize:'10px',color:'#555',fontFamily:'monospace',marginBottom:'6px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                        {door.lastEventTime} — {door.lastEvent}
                      </div>
                      <button
                        onClick={()=>doorCommand(door.id, isLocked ? 'Unlock' : 'Lock')}
                        style={{
                          width:'100%',height:'24px',background:'#0a0a0a',
                          border:'1px solid #333',borderRadius:0,
                          color:'#888',fontSize:'9px',fontWeight:700,
                          textTransform:'uppercase',letterSpacing:'0.05em',cursor:'pointer',
                        }}
                        onMouseEnter={e=>{(e.currentTarget as HTMLButtonElement).style.background='#1e3a5f';(e.currentTarget as HTMLButtonElement).style.color='#3b82f6';}}
                        onMouseLeave={e=>{(e.currentTarget as HTMLButtonElement).style.background='#0a0a0a';(e.currentTarget as HTMLButtonElement).style.color='#888';}}
                      >
                        {isLocked ? 'UNLOCK' : 'LOCK'}
                      </button>
                    </div>
                  );
              })}
            </div>
            {Object.values(doorStates).length === 0 && (
              <div style={{padding:'40px',textAlign:'center',color:'#444',fontSize:'11px'}}>No doors found</div>
            )}
          </div>
        )}

        {/* ── Attendance Tab ── */}
        {tab==='attendance' && (
          <div style={{padding:'20px'}}>
            {/* Summary */}
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px',marginBottom:'18px'}}>
              {[
                {l:'Currently In',     v:String(staffIn),                               c:'#22c55e'},
                {l:'Arrived Today',    v:String(attendance.length),                     c:'#3b82f6'},
                {l:'Departed Today',   v:String(attendance.filter(a=>a.status==='out').length), c:'#555555'},
                {l:'Peak Occupancy',   v:'34',                                           c:'#f59e0b'},
              ].map(s=>(
                <div key={s.l} style={{background:'#111',border:'1px solid #1a1a1a',borderRadius:'4px',padding:'12px'}}>
                  <div style={{fontSize:'22px',fontWeight:700,color:s.c}}>{s.v}</div>
                  <div style={{fontSize:'10px',color:'#555555',marginTop:'3px'}}>{s.l}</div>
                </div>
              ))}
            </div>

            {/* Hourly chart */}
            <div style={{marginBottom:'18px',background:'#111',border:'1px solid rgba(255,255,255,0.05)',borderRadius:'4px',padding:'14px'}}>
              <div style={{fontSize:'11px',fontWeight:600,color:'#a0a0a0',marginBottom:'12px'}}>Arrivals by Hour</div>
              <div style={{display:'flex',alignItems:'flex-end',gap:'8px',height:'60px'}}>
                {hourly.map(h=>{
                  const pct=hourly.length>0?Math.round((h.count/Math.max(...hourly.map(x=>x.count)))*100):0;
                  return (
                    <div key={h.h} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:'4px'}}>
                      <div style={{fontSize:'9px',color:'#3b82f6',fontWeight:600}}>{h.count}</div>
                      <div style={{width:'100%',height:`${pct}%`,minHeight:'4px',background:'linear-gradient(135deg,#3b82f6,#1d4ed8)',borderRadius:'3px 3px 0 0'}}/>
                      <div style={{fontSize:'8px',color:'#444444',whiteSpace:'nowrap'}}>{h.h.slice(0,2)}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Staff log */}
            <div style={{fontSize:'11px',fontWeight:600,color:'#a0a0a0',marginBottom:'8px'}}>Staff Log — Today</div>
            {initialLoading ? (
              <div>
                {Array.from({length:5}).map((_,i) => (
                  <div key={i} style={{ display:'flex', gap:'14px', padding:'9px 12px', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                    <div className="skeleton" style={{ height:'12px', width:'120px' }} />
                    <div className="skeleton" style={{ height:'12px', width:'80px' }} />
                    <div className="skeleton" style={{ height:'12px', width:'50px' }} />
                    <div className="skeleton" style={{ height:'12px', width:'50px' }} />
                    <div className="skeleton" style={{ height:'12px', width:'60px' }} />
                    <div className="skeleton" style={{ height:'20px', width:'55px', borderRadius:'4px' }} />
                  </div>
                ))}
              </div>
            ) : attendance.length === 0 ? (
              <EmptyState icon="👥" title="No attendance records" description="Badge-in and badge-out events from your access control system will appear here." size="md" />
            ) : (
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead>
                <tr style={{borderBottom:'1px solid #1a1a1a'}}>
                  {['Name','Department','Badge In','Badge Out','Duration','Status'].map(h=>(
                    <th key={h} style={{padding:'8px 12px',textAlign:'left',fontSize:'10px',fontWeight:700,color:'#555555',textTransform:'uppercase',letterSpacing:'0.05em'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {attendance.filter(a=>search===''||a.name.toLowerCase().includes(search.toLowerCase())||a.dept.toLowerCase().includes(search.toLowerCase())).map((a,i)=>(
                  <tr key={i} style={{borderBottom:'1px solid rgba(255,255,255,0.04)'}}
                    onMouseOver={e=>(e.currentTarget.style.background='#111')}
                    onMouseOut={e=>(e.currentTarget.style.background='transparent')}
                  >
                    <td style={{padding:'8px 12px',fontSize:'12px',color:'#e5e5e5',fontWeight:500}}>{a.name}</td>
                    <td style={{padding:'8px 12px',fontSize:'11px',color:'#555555'}}>{a.dept}</td>
                    <td style={{padding:'8px 12px',fontSize:'11px',color:'#a0a0a0',fontVariantNumeric:'tabular-nums'}}>{a.badgeIn}</td>
                    <td style={{padding:'8px 12px',fontSize:'11px',color:'#555555',fontVariantNumeric:'tabular-nums'}}>{a.badgeOut}</td>
                    <td style={{padding:'8px 12px',fontSize:'11px',color:'#555555'}}>{a.duration}</td>
                    <td style={{padding:'8px 12px'}}>
                      <span style={{fontSize:'9px',padding:'2px 8px',borderRadius:'4px',fontWeight:700,background:a.status==='in'?'rgba(34,197,94,0.1)':'rgba(100,116,139,0.1)',color:a.status==='in'?'#22c55e':'#555555'}}>
                        {a.status==='in'?'In Office':'Departed'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            )}
          </div>
        )}

        {/* ── Users Tab ── */}
        {tab==='users' && (() => {
          // When a controller is selected, filter to users with events on that controller
          const ctrlUserNames = activeController
            ? new Set(protegeEvents.filter(e => (e as any).controller === activeController.name || (e as any).controller === activeController.id).map(e => e.userName).filter(Boolean))
            : null;
          const baseCards = (ctrlUserNames && ctrlUserNames.size > 0)
            ? protegeCards.filter(c => ctrlUserNames.has(c.name) || ctrlUserNames.has(`${c.name} ${c.name2}`.trim()))
            : protegeCards;
          const usersFiltered = baseCards.filter(c =>
            search === '' ||
            c.name.toLowerCase().includes(search.toLowerCase()) ||
            (c.name2 || '').toLowerCase().includes(search.toLowerCase()) ||
            (c.department || '').toLowerCase().includes(search.toLowerCase()) ||
            c.id.includes(search)
          );
          if (!isProtegeOrg) {
            return <EmptyState icon="👤" title="User management requires Protege GX connection" description="Once connected, manage cardholders, access groups and credentials here." size="md" />;
          }
          if (protegeCards.length === 0) {
            return <EmptyState icon="🪪" title="Loading cardholders…" description="Fetching cardholder records from Protege GX." size="md" />;
          }

          function loadUserEvents(card: ProtegeCard) {
            if (selectedUser?.id === card.id) { setSelectedUser(null); setUserEvents([]); return; }
            setSelectedUser(card);
            setUserEvents([]);
            setUserEventsLoading(true);
            const token = getToken();
            apiGet(`/protege/events?userId=${card.id}&count=20`, token)
              .then(r => r.ok ? r.json() : null)
              .then(d => { setUserEvents(d?.events || []); })
              .catch(() => {})
              .finally(() => setUserEventsLoading(false));
          }

          return (
            <>
              {activeController && (
                <div style={{padding:'8px 16px',borderBottom:'1px solid #1a1a1a',background:'rgba(99,102,241,0.06)',display:'flex',alignItems:'center',gap:'8px'}}>
                  <span style={{fontSize:'10px',color:'#e5e5e5'}}>🔍 Showing users with recent events on:</span>
                  <span style={{fontSize:'11px',fontWeight:600,color:'#e5e5e5'}}>{activeController.name}</span>
                  {ctrlUserNames && ctrlUserNames.size === 0 && <span style={{fontSize:'10px',color:'#555555'}}>— no events found, showing all cardholders</span>}
                </div>
              )}
              <div style={{padding:'8px 16px',borderBottom:'1px solid rgba(255,255,255,0.04)',display:'flex',alignItems:'center',gap:'12px',justifyContent:'space-between'}}>
                <span style={{fontSize:'11px',color:'#555555'}}>
                  {usersFiltered.length}{activeController && ctrlUserNames && ctrlUserNames.size > 0 ? ` matched` : ` of ${protegeCards.length}`} cardholders
                  <span style={{marginLeft:8,color:'#555555'}}>· Search by name, ID or department</span>
                </span>
                <button onClick={()=>exportCSV(`cardholders-${csvDateStamp()}`,['ID','First Name','Last Name','Department'],protegeCards.map(c=>[c.id,c.name,c.name2||'',c.department||'']))}
                  style={{padding:'4px 12px',background:'rgba(34,197,94,0.1)',border:'1px solid rgba(34,197,94,0.25)',borderRadius:'6px',color:'#22c55e',fontSize:'11px',fontWeight:600,cursor:'pointer'}}>↓ Export CSV</button>
              </div>
              <table style={{width:'100%',borderCollapse:'collapse'}}>
                <thead>
                  <tr style={{borderBottom:'1px solid #1a1a1a'}}>
                    {['ID','First Name','Last Name','Department',''].map((h,i)=>(
                      <th key={i} style={{padding:'9px 16px',textAlign:'left',fontSize:'10px',fontWeight:700,color:'#555555',textTransform:'uppercase',letterSpacing:'0.06em'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {usersFiltered.slice(0, 200).map(c => {
                    const isExpanded = selectedUser?.id === c.id;
                    return (
                      <React.Fragment key={c.id}>
                        <tr
                          onClick={() => loadUserEvents(c)}
                          style={{borderBottom:'1px solid rgba(255,255,255,0.04)',cursor:'pointer',background:isExpanded?'rgba(59,130,246,0.06)':'transparent'}}
                          onMouseOver={e=>(e.currentTarget.style.background=isExpanded?'rgba(59,130,246,0.06)':'rgba(255,255,255,0.02)')}
                          onMouseOut={e=>(e.currentTarget.style.background=isExpanded?'rgba(59,130,246,0.06)':'transparent')}
                        >
                          <td style={{padding:'9px 16px',fontSize:'11px',color:'#555555',fontFamily:'monospace'}}>{c.id}</td>
                          <td style={{padding:'9px 16px',fontSize:'12px',color:'#e5e5e5',fontWeight:500}}>{c.name}</td>
                          <td style={{padding:'9px 16px',fontSize:'12px',color:'#a0a0a0'}}>{c.name2 || '—'}</td>
                          <td style={{padding:'9px 16px',fontSize:'11px',color:'#555555'}}>{c.department || '—'}</td>
                          <td style={{padding:'9px 16px',fontSize:'10px',color:'#3b82f6'}}>{isExpanded ? '▲ Hide' : '▼ Events'}</td>
                        </tr>
                        {isExpanded && (
                          <tr style={{background:'rgba(15,23,42,0.6)'}}>
                            <td colSpan={5} style={{padding:'0'}}>
                              <div style={{padding:'12px 20px',borderBottom:'1px solid #1a1a1a'}}>
                                <div style={{fontSize:'11px',fontWeight:600,color:'#a0a0a0',marginBottom:'8px'}}>
                                  Recent events for {c.name} {c.name2}
                                  <span style={{marginLeft:8,fontSize:'10px',color:'#555555',fontWeight:400}}>User ID: {c.id}</span>
                                </div>
                                {userEventsLoading ? (
                                  <div style={{fontSize:'11px',color:'#555555'}}>Loading events…</div>
                                ) : userEvents.length === 0 ? (
                                  <div style={{fontSize:'11px',color:'#555555'}}>No recent events found for this cardholder.</div>
                                ) : (
                                  <table style={{width:'100%',borderCollapse:'collapse'}}>
                                    <thead>
                                      <tr>
                                        {['Time','Event','Door','Controller'].map(h=>(
                                          <th key={h} style={{padding:'5px 10px',textAlign:'left',fontSize:'9px',fontWeight:700,color:'#444444',textTransform:'uppercase'}}>{h}</th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {userEvents.map((ev,i) => (
                                        <tr key={i} style={{borderTop:'1px solid rgba(255,255,255,0.03)'}}>
                                          <td style={{padding:'5px 10px',fontSize:'10px',color:'#555555',fontVariantNumeric:'tabular-nums'}}>{ev.timestamp || '—'}</td>
                                          <td style={{padding:'5px 10px',fontSize:'10px',color:'#a0a0a0'}}>{ev.eventDesc || ev.eventType || '—'}</td>
                                          <td style={{padding:'5px 10px',fontSize:'10px',color:'#555555'}}>{ev.deviceName || '—'}</td>
                                          <td style={{padding:'5px 10px',fontSize:'10px',color:'#555555'}}>{(ev as any).controller || '—'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              {usersFiltered.length > 200 && (
                <div style={{padding:'10px 16px',textAlign:'center',fontSize:'11px',color:'#555555'}}>Showing first 200 of {usersFiltered.length} — use search to filter</div>
              )}
              {/* Credential actions bar */}
              <div style={{padding:'12px 16px',borderTop:'1px solid rgba(255,255,255,0.06)',background:'rgba(0,0,0,0.2)',display:'flex',gap:'8px',flexWrap:'wrap'}}>
                <div style={{fontSize:'11px',color:'#555555',alignSelf:'center',flex:1}}>Select a cardholder to view events, or use actions below:</div>
                <button onClick={()=>showNotif('Create credential — open Protege GX to issue a new card or PIN')}
                  style={{padding:'6px 14px',background:'rgba(34,197,94,0.1)',border:'1px solid rgba(34,197,94,0.25)',borderRadius:'7px',color:'#22c55e',fontSize:'11px',fontWeight:600,cursor:'pointer'}}>+ Issue Credential</button>
                <button onClick={()=>showNotif('To deactivate a card: select the cardholder above, then click Deactivate')}
                  style={{padding:'6px 14px',background:'rgba(239,68,68,0.08)',border:'1px solid rgba(239,68,68,0.2)',borderRadius:'7px',color:'#ef4444',fontSize:'11px',fontWeight:600,cursor:'pointer'}}>Deactivate Card</button>
                <button onClick={()=>showNotif('Modify access level — select cardholder then choose a different access level')}
                  style={{padding:'6px 14px',background:'rgba(245,158,11,0.08)',border:'1px solid rgba(245,158,11,0.2)',borderRadius:'7px',color:'#f59e0b',fontSize:'11px',fontWeight:600,cursor:'pointer'}}>Modify Access Level</button>
              </div>
            </>
          );
        })()}

        {/* ── Site Map Tab ── */}
        {tab==='map' && (() => {
          // All available doors (use liveDoors so controller filter doesn't hide them in edit mode)
          const allMapDoorSource = liveDoors;

          // Doors for the map: only those with saved positions
          // Build all placed doors with controllerId metadata for filtering
          const placedDoorsAll = allMapDoorSource
            .filter(d => doorPositions[String(d.id)])
            .map(d => ({
              id: String(d.id),
              name: d.name,
              zone: 'lobby',
              status: d.status === 'forced-open' ? 'forced' : d.status as any,
              x: doorPositions[String(d.id)].x,
              y: doorPositions[String(d.id)].y,
              horizontal: false,
              _controllerId: (d as any).controllerId || '',
              _controller: (d as any).controller || '',
            }));
          // Filter to active controller when one is selected
          const placedDoors = activeController
            ? placedDoorsAll.filter(d =>
                d._controllerId === activeController.id ||
                d._controller === activeController.name ||
                d._controller === activeController.id ||
                (!d._controllerId && !d._controller)
              )
            : placedDoorsAll;
          // Use fixed positions for demo org
          const mapDoors = isProtegeOrg ? placedDoors : Object.values(doorStates).map(d => ({
            id: d.id.toString(), name: d.name, zone: 'lobby',
            status: d.status === 'forced-open' ? 'forced' : d.status as any,
            x: [140,561,250,678,405,170][d.id-1] || 300,
            y: [376,79,268,215,381,215][d.id-1] || 250,
            horizontal: [true,false,false,false,true,false][d.id-1] || false,
          }));

          // All available cameras for placement
          const allCameraSource: MapCamera[] = DEFAULT_CAMERAS;
          const unplacedCameras = allCameraSource.filter(c => !cameraPositions[c.id]);
          const placedCameras: MapCamera[] = allCameraSource
            .filter(c => cameraPositions[c.id])
            .map(c => ({ ...c, cx: cameraPositions[c.id].x, cy: cameraPositions[c.id].y }));

          // Unplaced doors
          const unplacedDoors = allMapDoorSource.filter(d => !doorPositions[String(d.id)]);

          function handleMapDrop(e: React.DragEvent<HTMLDivElement>) {
            e.preventDefault();
            if (!mapDropRef.current) return;
            const rect = mapDropRef.current.getBoundingClientRect();
            const svgX = Math.round(((e.clientX - rect.left) / rect.width) * 820);
            const svgY = Math.round(((e.clientY - rect.top) / rect.height) * 500);
            const cx = Math.max(20, Math.min(800, svgX));
            const cy = Math.max(20, Math.min(480, svgY));

            const doorId = e.dataTransfer.getData('doorId');
            if (doorId) {
              const newPos = {...doorPositions, [doorId]: {x: cx, y: cy}};
              setDoorPositions(newPos);
              localStorage.setItem(mapKey('siteMapDoorPositions'), JSON.stringify(newPos));
              _saveMapToAPI(newPos, cameraPositions);
            }
            const cameraId = e.dataTransfer.getData('cameraId');
            if (cameraId) {
              const newPos = {...cameraPositions, [cameraId]: {x: cx, y: cy}};
              setCameraPositions(newPos);
              localStorage.setItem(mapKey('siteMapCameraPositions'), JSON.stringify(newPos));
              _saveMapToAPI(doorPositions, newPos);
            }
          }

          function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
            const file = e.target.files?.[0];
            if (!file) return;
            const reader = new FileReader();
            reader.onload = (ev) => {
              const dataUrl = ev.target?.result as string;
              setMapBgImage(dataUrl);
              localStorage.setItem(mapKey('siteMapBgImage'), dataUrl);
            };
            reader.readAsDataURL(file);
            e.target.value = '';
          }

          return (
            <div style={{padding:'16px'}}>
              {/* Controller selector — inline chip strip at top of map tab */}
              {(isProtegeOrg ? liveControllers : displayControllers.map(c=>({id:c.id,name:c.name,online:c.status==='online',siteId:''}))).length > 0 && (
                <div style={{display:'flex',alignItems:'center',gap:'6px',marginBottom:'12px',flexWrap:'wrap'}}>
                  <span style={{fontSize:'10px',color:'#555555',whiteSpace:'nowrap',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.05em'}}>Controller:</span>
                  <button
                    onClick={() => setActiveController(null)}
                    style={{padding:'4px 10px',borderRadius:'20px',fontSize:'10px',fontWeight:600,cursor:'pointer',
                      background: !activeController ? 'rgba(59,130,246,0.18)' : 'rgba(255,255,255,0.04)',
                      border: !activeController ? '1px solid rgba(59,130,246,0.4)' : '1px solid rgba(255,255,255,0.08)',
                      color: !activeController ? '#3b82f6' : '#555555',whiteSpace:'nowrap',
                    }}>All Sites</button>
                  {(isProtegeOrg ? liveControllers : displayControllers.map(c=>({id:c.id,name:c.name,online:c.status==='online',siteId:''}))).map(c => {
                    const isActive = activeController?.id === c.id;
                    const col = c.online ? '#22c55e' : '#ef4444';
                    return (
                      <button key={c.id}
                        onClick={() => setActiveController({id:c.id,name:c.name,online:c.online})}
                        style={{display:'flex',alignItems:'center',gap:'5px',padding:'4px 10px',borderRadius:'20px',fontSize:'10px',fontWeight:600,cursor:'pointer',
                          background: isActive ? `${col}18` : 'rgba(255,255,255,0.04)',
                          border: isActive ? `1px solid ${col}40` : '1px solid rgba(255,255,255,0.08)',
                          color: isActive ? col : '#555555',whiteSpace:'nowrap',
                        }}>
                        <div style={{width:5,height:5,borderRadius:'50%',background:col,flexShrink:0}}/>
                        {c.name.replace(/Controller|Main Controller/gi,'').trim() || c.name}
                      </button>
                    );
                  })}
                </div>
              )}
              {/* Alarm focus banner — set by SOC Dashboard "View on Map" */}
              {mapFocusBanner && (
                <div style={{marginBottom:'12px',padding:'10px 14px',background:'rgba(239,68,68,0.08)',border:'1px solid rgba(239,68,68,0.3)',borderRadius:'4px',display:'flex',alignItems:'center',gap:'10px',flexWrap:'wrap'}}>
                  <div style={{width:8,height:8,borderRadius:'50%',background:'#ef4444',flexShrink:0,animation:'pulse 1.5s infinite'}} />
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:'12px',fontWeight:700,color:'#fca5a5'}}>Alarm Focus: {mapFocusBanner.alarmType}</div>
                    <div style={{fontSize:'11px',color:'#a0a0a0',marginTop:'2px'}}>{mapFocusBanner.site} · {mapFocusBanner.location} · {mapFocusBanner.time}</div>
                  </div>
                  <button onClick={() => setMapFocusBanner(null)} style={{background:'none',border:'none',color:'#555555',cursor:'pointer',fontSize:'14px',padding:'0 4px'}}>✕</button>
                </div>
              )}
              {/* Hidden file input */}
              <input ref={bgUploadRef} type="file" accept="image/*" style={{display:'none'}} onChange={handleImageUpload} />

              {/* Toolbar */}
              <div style={{marginBottom:'10px',display:'flex',gap:'8px',flexWrap:'wrap',alignItems:'center'}}>
                <div style={{flex:1,fontSize:'11px',color:'#a0a0a0'}}>
                  <span style={{color:'#3b82f6',fontWeight:600}}>
                    {activeController ? `Map: ${activeController.name}` : 'Access Control Map (Global)'}
                  </span>
                  {' '}— {editMapLayout ? 'Drag doors & cameras from the list onto the map to place them.' : 'Click Edit Layout to drag & place doors on the map.'}
                  {isProtegeOrg && editMapLayout && placedDoors.length === 0 && <span style={{marginLeft:8,color:'#f59e0b'}}> Drag doors from the list on the left.</span>}
                </div>
                {/* Zone toggle */}
                <button onClick={() => { const n = !showMapZones; setShowMapZones(n); localStorage.setItem('siteMapShowZones', String(n)); }}
                  style={{padding:'4px 10px',borderRadius:'6px',fontSize:'11px',fontWeight:600,cursor:'pointer',
                    background: showMapZones ? 'rgba(99,102,241,0.12)' : 'rgba(255,255,255,0.04)',
                    border: showMapZones ? '1px solid rgba(99,102,241,0.3)' : '1px solid rgba(255,255,255,0.08)',
                    color: showMapZones ? '#a5b4fc' : '#555555',
                  }}
                >{showMapZones ? '🏢 Zones on' : '🏢 Zones off'}</button>
                {/* Image upload */}
                <button onClick={() => bgUploadRef.current?.click()}
                  style={{padding:'4px 10px',borderRadius:'6px',fontSize:'11px',fontWeight:600,cursor:'pointer',background:'#111111',border:'1px solid rgba(255,255,255,0.1)',color:'#a0a0a0'}}>
                  🖼 {mapBgImage ? 'Change floor plan' : 'Upload floor plan'}
                </button>
                {mapBgImage && (
                  <button onClick={() => { setMapBgImage(''); localStorage.removeItem(mapKey('siteMapBgImage')); }}
                    style={{padding:'4px 8px',borderRadius:'6px',fontSize:'11px',cursor:'pointer',background:'rgba(239,68,68,0.08)',border:'1px solid rgba(239,68,68,0.2)',color:'#ef4444'}}>✕ Remove</button>
                )}
                {/* Draw mode toggle */}
                <button onClick={() => setAcDrawMode(p => p === 'off' ? 'pencil' : 'off')}
                  style={{padding:'4px 10px',borderRadius:'6px',fontSize:'11px',fontWeight:600,cursor:'pointer',
                    background: acDrawMode !== 'off' ? 'rgba(168,85,247,0.12)' : 'rgba(255,255,255,0.04)',
                    border: acDrawMode !== 'off' ? '1px solid rgba(168,85,247,0.4)' : '1px solid rgba(255,255,255,0.1)',
                    color: acDrawMode !== 'off' ? '#c084fc' : '#94a3b8',
                  }}>✏️ {acDrawMode !== 'off' ? 'Drawing' : 'Draw'}</button>
                {acDrawMode !== 'off' && (<>
                  <button onClick={() => setAcDrawMode(p => p === 'pencil' ? 'eraser' : 'pencil')}
                    style={{padding:'4px 8px',borderRadius:'6px',fontSize:'11px',fontWeight:600,cursor:'pointer',background:'rgba(245,158,11,0.1)',border:'1px solid rgba(245,158,11,0.3)',color:'#f59e0b'}}>
                    {acDrawMode === 'pencil' ? '✏️ Pencil' : '⬜ Eraser'}
                  </button>
                  <input type="color" value={acDrawColor} onChange={e => setAcDrawColor(e.target.value)}
                    style={{width:26,height:22,padding:2,borderRadius:4,border:'1px solid rgba(255,255,255,0.1)',background:'transparent',cursor:'pointer'}} title="Pen colour" />
                  <select value={acDrawSize} onChange={e => setAcDrawSize(Number(e.target.value))}
                    style={{padding:'3px 6px',borderRadius:'5px',fontSize:'11px',background:'#111111',border:'1px solid rgba(255,255,255,0.1)',color:'#e5e5e5',cursor:'pointer'}}>
                    {[2,4,6,10,16].map(s=><option key={s} value={s} style={{background:'#111111'}}>{s}px</option>)}
                  </select>
                  <button onClick={() => {
                    const canvas = acCanvasRef.current;
                    if (!canvas) return;
                    canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
                    const drawKey = activeController ? `ac_drawing_${activeController.id}` : 'ac_drawing_global';
                    localStorage.removeItem(drawKey);
                  }} style={{padding:'4px 8px',borderRadius:'6px',fontSize:'11px',cursor:'pointer',background:'rgba(239,68,68,0.08)',border:'1px solid rgba(239,68,68,0.2)',color:'#ef4444'}}>Clear</button>
                </>)}
                {/* Edit layout toggle */}
                <button onClick={() => setEditMapLayout(p => !p)}
                  style={{padding:'4px 12px',borderRadius:'6px',fontSize:'11px',fontWeight:600,cursor:'pointer',
                    background: editMapLayout ? 'rgba(245,158,11,0.15)' : 'rgba(255,255,255,0.05)',
                    border: editMapLayout ? '1px solid rgba(245,158,11,0.4)' : '1px solid rgba(255,255,255,0.1)',
                    color: editMapLayout ? '#f59e0b' : '#94a3b8',
                  }}
                >{editMapLayout ? '✓ Done' : '✏️ Edit layout'}</button>
                {editMapLayout && (placedDoors.length > 0 || placedCameras.length > 0) && (
                  <button onClick={() => {
                    setDoorPositions({}); localStorage.removeItem(mapKey('siteMapDoorPositions'));
                    setCameraPositions({}); localStorage.removeItem(mapKey('siteMapCameraPositions'));
                    _saveMapToAPI({}, {});
                  }}
                    style={{padding:'4px 10px',borderRadius:'6px',fontSize:'11px',cursor:'pointer',background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.25)',color:'#ef4444'}}>
                    Reset layout
                  </button>
                )}
              </div>

              <div style={{display:'flex',gap:'12px'}}>
                {/* Left panel: always visible for Protege org */}
                {isProtegeOrg && (
                  <div style={{width:'200px',flexShrink:0,display:'flex',flexDirection:'column',gap:'10px'}}>

                    {/* Doors list */}
                    <div style={{background:'#111',border:'1px solid #1a1a1a',borderRadius:'4px',overflow:'hidden'}}>
                      <div style={{padding:'9px 12px',borderBottom:'1px solid #1a1a1a',fontSize:'11px',fontWeight:600,color:'#a0a0a0',display:'flex',alignItems:'center',gap:'6px'}}>
                        <span>🚪</span> Doors
                        {allMapDoorSource.length > 0 && <span style={{fontSize:'10px',color:'#555555',fontWeight:400}}>({allMapDoorSource.length})</span>}
                        {editMapLayout && unplacedDoors.length === 0 && allMapDoorSource.length > 0 && (
                          <span style={{fontSize:'9px',color:'#22c55e',marginLeft:'auto'}}>All placed</span>
                        )}
                      </div>
                      <div style={{maxHeight:'260px',overflowY:'auto'}}>
                        {allMapDoorSource.length === 0 && (
                          <div style={{padding:'14px',fontSize:'11px',color:'#555555',textAlign:'center'}}>Doors loading...</div>
                        )}
                        {/* In edit mode: unplaced doors are draggable */}
                        {editMapLayout && unplacedDoors.map(d => (
                          <div key={d.id} draggable
                            onDragStart={e => { e.dataTransfer.setData('doorId', String(d.id)); e.dataTransfer.effectAllowed = 'move'; }}
                            style={{display:'flex',flexDirection:'column',gap:'2px',padding:'7px 12px',borderBottom:'1px solid rgba(255,255,255,0.04)',cursor:'grab',userSelect:'none'}}
                            onMouseOver={e=>(e.currentTarget as HTMLDivElement).style.background='rgba(255,255,255,0.04)'}
                            onMouseOut={e=>(e.currentTarget as HTMLDivElement).style.background='transparent'}
                          >
                            <span style={{fontSize:'11px',color:'#e5e5e5',fontWeight:500,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{d.name}</span>
                            <span style={{fontSize:'9px',color:'#3b82f6'}}>Drag to place</span>
                          </div>
                        ))}
                        {/* In edit mode: placed doors show with remove button */}
                        {editMapLayout && allMapDoorSource.filter(d => doorPositions[String(d.id)]).map(d => (
                          <div key={d.id} style={{display:'flex',alignItems:'center',gap:'6px',padding:'7px 12px',borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                            <span style={{fontSize:'10px',color:'#22c55e'}}>✓</span>
                            <span style={{fontSize:'11px',color:'#555555',flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{d.name}</span>
                            <button onClick={() => {
                              const newPos = {...doorPositions};
                              delete newPos[String(d.id)];
                              setDoorPositions(newPos);
                              localStorage.setItem(mapKey('siteMapDoorPositions'), JSON.stringify(newPos));
                              _saveMapToAPI(newPos, cameraPositions);
                            }} style={{background:'none',border:'none',color:'#ef4444',cursor:'pointer',fontSize:'11px',padding:'0 2px'}}>x</button>
                          </div>
                        ))}
                        {/* In view mode: all doors listed with status */}
                        {!editMapLayout && allMapDoorSource.map(d => {
                          const isOnMap = !!doorPositions[String(d.id)];
                          return (
                            <div key={d.id}
                              style={{display:'flex',alignItems:'center',gap:'6px',padding:'7px 12px',borderBottom:'1px solid rgba(255,255,255,0.04)'}}
                            >
                              <div style={{width:6,height:6,borderRadius:'50%',flexShrink:0,
                                background: d.status==='locked'?'#555555':d.status==='unlocked'?'#22c55e':d.status==='open'?'#3b82f6':'#ef4444'}}/>
                              <span style={{fontSize:'11px',color: isOnMap ? '#e2e8f0' : '#555555',flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{d.name}</span>
                              {!isOnMap && <span style={{fontSize:'9px',color:'#444444'}}>not placed</span>}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Cameras list */}
                    <div style={{background:'#111',border:'1px solid #1a1a1a',borderRadius:'4px',overflow:'hidden'}}>
                      <div style={{padding:'9px 12px',borderBottom:'1px solid #1a1a1a',fontSize:'11px',fontWeight:600,color:'#a0a0a0',display:'flex',alignItems:'center',gap:'6px'}}>
                        <span>📹</span> Cameras
                        <span style={{fontSize:'10px',color:'#555555',fontWeight:400}}>({DEFAULT_CAMERAS.length})</span>
                        {editMapLayout && unplacedCameras.length === 0 && DEFAULT_CAMERAS.length > 0 && (
                          <span style={{fontSize:'9px',color:'#22c55e',marginLeft:'auto'}}>All placed</span>
                        )}
                      </div>
                      <div style={{maxHeight:'220px',overflowY:'auto'}}>
                        {/* Edit mode: unplaced cameras draggable */}
                        {editMapLayout && unplacedCameras.map(c => (
                          <div key={c.id} draggable
                            onDragStart={e => { e.dataTransfer.setData('cameraId', c.id); e.dataTransfer.effectAllowed = 'move'; }}
                            style={{display:'flex',flexDirection:'column',gap:'2px',padding:'7px 12px',borderBottom:'1px solid rgba(255,255,255,0.04)',cursor:'grab',userSelect:'none'}}
                            onMouseOver={e=>(e.currentTarget as HTMLDivElement).style.background='rgba(255,255,255,0.04)'}
                            onMouseOut={e=>(e.currentTarget as HTMLDivElement).style.background='transparent'}
                          >
                            <span style={{fontSize:'11px',color:'#e5e5e5',fontWeight:500,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.name}</span>
                            <span style={{fontSize:'9px',color:'#3b82f6'}}>Drag to place</span>
                          </div>
                        ))}
                        {/* Edit mode: placed cameras with remove */}
                        {editMapLayout && DEFAULT_CAMERAS.filter(c => cameraPositions[c.id]).map(c => (
                          <div key={c.id} style={{display:'flex',alignItems:'center',gap:'6px',padding:'7px 12px',borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                            <span style={{fontSize:'10px',color:'#22c55e'}}>✓</span>
                            <span style={{fontSize:'11px',color:'#555555',flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.name}</span>
                            <button onClick={() => {
                              const newPos = {...cameraPositions};
                              delete newPos[c.id];
                              setCameraPositions(newPos);
                              localStorage.setItem(mapKey('siteMapCameraPositions'), JSON.stringify(newPos));
                              _saveMapToAPI(doorPositions, newPos);
                            }} style={{background:'none',border:'none',color:'#ef4444',cursor:'pointer',fontSize:'11px',padding:'0 2px'}}>x</button>
                          </div>
                        ))}
                        {/* View mode: show all cameras with status dot */}
                        {!editMapLayout && DEFAULT_CAMERAS.map(c => {
                          const isOnMap = !!cameraPositions[c.id];
                          const statusColor = c.status === 'online' ? '#22c55e' : c.status === 'motion' ? '#f59e0b' : c.status === 'alarm' ? '#ef4444' : '#555555';
                          return (
                            <div key={c.id}
                              onClick={() => setCameraFeedPopup({id:c.id,name:c.name,status:c.status})}
                              style={{display:'flex',alignItems:'center',gap:'6px',padding:'7px 12px',borderBottom:'1px solid rgba(255,255,255,0.04)',cursor:'pointer'}}
                              onMouseOver={e=>(e.currentTarget as HTMLDivElement).style.background='#111111'}
                              onMouseOut={e=>(e.currentTarget as HTMLDivElement).style.background='transparent'}
                            >
                              <div style={{width:6,height:6,borderRadius:'50%',flexShrink:0,background:statusColor}}/>
                              <span style={{fontSize:'11px',color:isOnMap?'#e2e8f0':'#555555',flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.name}</span>
                              {!isOnMap && <span style={{fontSize:'9px',color:'#444444'}}>not placed</span>}
                              {isOnMap && <span style={{fontSize:'9px',color:'#3b82f6'}}>▶</span>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* Map */}
                <div ref={mapDropRef} style={{flex:1}}
                  onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                  onDrop={handleMapDrop}
                >
                  {/* Map controls (non-edit mode) */}
                  {!editMapLayout && (
                    <div style={{display:'flex',gap:'6px',marginBottom:'10px',flexWrap:'wrap'}}>
                      {[
                        { label:'🔒 Lock All',        color:'#555555', cmd:'Lock',          desc:'Lock all doors' },
                        { label:'🔓 Unlock All',       color:'#22c55e', cmd:'Unlock',        desc:'Unlock all doors' },
                        { label:'🚪 Momentary Open',   color:'#3b82f6', cmd:'Open',          desc:'Momentarily open all' },
                        { label:'⚠️ Lockdown',         color:'#ef4444', cmd:'Force Lock',    desc:'Force secure all doors' },
                        { label:'↩ Clear Override',   color:'#a855f7', cmd:'Clear Override',desc:'Return to schedule' },
                      ].map(({label, color, cmd, desc}) => (
                        <button key={cmd}
                          onClick={() => {
                            const target = mapDoors.length > 0 ? mapDoors : Object.values(doorStates);
                            const count = target.length;
                            if (count === 0) { showNotif('No doors on map — place doors first'); return; }
                            showNotif(`${cmd} sent to ${count} door${count !== 1 ? 's' : ''}`);
                            // Send to each door via Protege if connected
                            if (protegeStatus === 'connected') {
                              const token2 = localStorage.getItem('lemai_token') || '';
                              const cmdMap: Record<string,string> = {'Lock':'lock','Unlock':'unlock','Open':'open','Force Lock':'lock','Clear Override':'cycle'};
                              target.forEach((d: any) => {
                                fetch('/api/protege/door-command', {
                                  method:'POST',
                                  headers:{'Authorization':`Bearer ${token2}`,'Content-Type':'application/json'},
                                  body: JSON.stringify({doorIndex: Number(d.id), command: cmdMap[cmd] || 'open', controllerId: 0, siteId: 6}),
                                }).catch(()=>{});
                              });
                            }
                          }}
                          title={desc}
                          style={{padding:'5px 12px',borderRadius:'7px',fontSize:'11px',fontWeight:600,cursor:'pointer',
                            background:`${color}14`,border:`1px solid ${color}30`,color,whiteSpace:'nowrap'}}
                          onMouseOver={e=>(e.currentTarget.style.background=`${color}22`)}
                          onMouseOut={e=>(e.currentTarget.style.background=`${color}14`)}
                        >{label}</button>
                      ))}
                      <div style={{flex:1}}/>
                      {(isProtegeOrg ? placedDoors : mapDoors).length > 0 && (
                        <span style={{fontSize:'10px',color:'#444444',alignSelf:'center'}}>
                          {(isProtegeOrg ? placedDoors : mapDoors).length} door{(isProtegeOrg ? placedDoors : mapDoors).length !== 1 ? 's' : ''} on map
                        </span>
                      )}
                    </div>
                  )}
                  <div style={{position:'relative'}}>
                    <SiteMap
                      mode="access"
                      cameras={isProtegeOrg ? placedCameras : (isDemoOrg ? DEFAULT_CAMERAS : placedCameras)}
                      doors={mapDoors}
                      outputs={DEFAULT_OUTPUTS}
                      alarmZones={DEFAULT_ALARM_ZONES}
                      onDoorClick={door => {/* handled by onDoorControl popup */}}
                      onDoorControl={(id, cmd) => {
                        const cmdLabel = cmd === 'lock' ? 'Lock' : cmd === 'unlock' ? 'Unlock' : 'Open';
                        const numId = Number(id);
                        if (!isNaN(numId) && doorStates[numId]) {
                          doorCommand(numId, cmdLabel);
                        } else {
                          const d = Object.values(doorStates).find(x => String(x.id) === id);
                          if (d) doorCommand(d.id, cmdLabel);
                          else {
                            // Protege door by name lookup
                            const byName = liveDoors.find(x => String(x.id) === id);
                            if (byName) {
                              showNotif(`${cmdLabel} sent to ${byName.name}`);
                              if (protegeStatus === 'connected') {
                                const tok = localStorage.getItem('lemai_token')||'';
                                const cmdMap: Record<string,string>={'Lock':'lock','Unlock':'unlock','Open':'open'};
                                fetch('/api/protege/door-command',{method:'POST',headers:{Authorization:`Bearer ${tok}`,'Content-Type':'application/json'},body:JSON.stringify({doorIndex:Number(byName.id),command:cmdMap[cmdLabel]||'open',controllerId:0,siteId:0})}).catch(()=>{});
                              }
                            }
                          }
                        }
                      }}
                      onOutputClick={out => showNotif(`Output: ${out.name} — ${out.status}`)}
                      onZoneClick={zone => showNotif(`Zone: ${zone.name} — ${zone.status}`)}
                      height={440}
                      editLayout={editMapLayout}
                      bgImage={mapBgImage || undefined}
                      showZones={showMapZones}
                      onPositionChange={(id, x, y) => {
                        const newPos = {...doorPositions, [id]: {x, y}};
                        setDoorPositions(newPos);
                        localStorage.setItem(mapKey('siteMapDoorPositions'), JSON.stringify(newPos));
                        _saveMapToAPI(newPos, cameraPositions);
                      }}
                      onRemoveDoor={id => {
                        const newPos = {...doorPositions};
                        delete newPos[id];
                        setDoorPositions(newPos);
                        localStorage.setItem(mapKey('siteMapDoorPositions'), JSON.stringify(newPos));
                        _saveMapToAPI(newPos, cameraPositions);
                      }}
                      onCameraClick={cam => setCameraFeedPopup({id:cam.id, name:cam.name, status:cam.status})}
                      onCameraPositionChange={(id, x, y) => {
                        const newPos = {...cameraPositions, [id]:{x,y}};
                        setCameraPositions(newPos);
                        localStorage.setItem(mapKey('siteMapCameraPositions'), JSON.stringify(newPos));
                        _saveMapToAPI(doorPositions, newPos);
                      }}
                      onRemoveCamera={id => {
                        const newPos = {...cameraPositions};
                        delete newPos[id];
                        setCameraPositions(newPos);
                        localStorage.setItem(mapKey('siteMapCameraPositions'), JSON.stringify(newPos));
                        _saveMapToAPI(doorPositions, newPos);
                      }}
                    />
                    {/* Drawing canvas overlay */}
                    <canvas
                      ref={acCanvasRef}
                      width={820} height={440}
                      style={{
                        position:'absolute', top:0, left:0, width:'100%', height:'100%',
                        cursor: acDrawMode !== 'off' ? (acDrawMode === 'eraser' ? 'cell' : 'crosshair') : 'default',
                        pointerEvents: acDrawMode !== 'off' ? 'auto' : 'none',
                        borderRadius:'inherit',
                      }}
                      onMouseDown={e => {
                        if (acDrawMode === 'off') return;
                        acDrawingActive.current = true;
                        const canvas = acCanvasRef.current!;
                        const rect = canvas.getBoundingClientRect();
                        const pt = { x: (e.clientX - rect.left) * (canvas.width / rect.width), y: (e.clientY - rect.top) * (canvas.height / rect.height) };
                        acLastPt.current = pt;
                        const ctx = canvas.getContext('2d');
                        if (!ctx) return;
                        ctx.beginPath();
                        if (acDrawMode === 'eraser') {
                          ctx.globalCompositeOperation = 'destination-out';
                          ctx.arc(pt.x, pt.y, acDrawSize * 2, 0, Math.PI * 2); ctx.fill();
                          ctx.globalCompositeOperation = 'source-over';
                        } else {
                          ctx.fillStyle = acDrawColor;
                          ctx.arc(pt.x, pt.y, acDrawSize / 2, 0, Math.PI * 2); ctx.fill();
                        }
                      }}
                      onMouseMove={e => {
                        if (!acDrawingActive.current || acDrawMode === 'off' || !acLastPt.current) return;
                        const canvas = acCanvasRef.current!;
                        const ctx = canvas.getContext('2d');
                        if (!ctx) return;
                        const rect = canvas.getBoundingClientRect();
                        const pt = { x: (e.clientX - rect.left) * (canvas.width / rect.width), y: (e.clientY - rect.top) * (canvas.height / rect.height) };
                        ctx.beginPath();
                        ctx.moveTo(acLastPt.current.x, acLastPt.current.y);
                        ctx.lineTo(pt.x, pt.y);
                        ctx.lineCap = 'round'; ctx.lineJoin = 'round';
                        if (acDrawMode === 'eraser') {
                          ctx.globalCompositeOperation = 'destination-out';
                          ctx.lineWidth = acDrawSize * 4;
                          ctx.strokeStyle = 'rgba(0,0,0,1)';
                        } else {
                          ctx.globalCompositeOperation = 'source-over';
                          ctx.lineWidth = acDrawSize;
                          ctx.strokeStyle = acDrawColor;
                        }
                        ctx.stroke();
                        acLastPt.current = pt;
                      }}
                      onMouseUp={() => {
                        if (!acDrawingActive.current) return;
                        acDrawingActive.current = false;
                        acLastPt.current = null;
                        const canvas = acCanvasRef.current;
                        if (!canvas || acDrawMode === 'off') return;
                        const drawKey = activeController ? `ac_drawing_${activeController.id}` : 'ac_drawing_global';
                        try { localStorage.setItem(drawKey, canvas.toDataURL('image/png')); } catch {}
                      }}
                      onMouseLeave={() => { acDrawingActive.current = false; acLastPt.current = null; }}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── Intercom Tab ── */}
        {tab==='intercom' && (
          <div style={{padding:'16px'}}>
            {/* Skeleton on initial load */}
            {initialLoading && (
              <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:'10px'}}>
                {Array.from({length:4}).map((_,i)=>(
                  <div key={i} style={{background:'#111',borderRadius:'4px',padding:'14px',height:'80px'}}>
                    <div className="skeleton" style={{height:'12px',width:'60%',marginBottom:'8px'}}/>
                    <div className="skeleton" style={{height:'10px',width:'40%'}}/>
                  </div>
                ))}
              </div>
            )}
            {!initialLoading && <>
            {/* Stats strip */}
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px',marginBottom:'16px'}}>
              {[
                {l:'Online Stations',  v:String(stations.filter(s=>s.status!=='offline').length), c:'#22c55e'},
                {l:'Ringing Now',      v:String(stations.filter(s=>s.status==='ringing').length),  c:'#ef4444'},
                {l:'Missed Today',     v:'2',  c:'#f59e0b'},
                {l:'Total Calls Today',v:'6',  c:'#3b82f6'},
              ].map(s=>(
                <div key={s.l} style={{background:'#111',border:'1px solid #1a1a1a',borderRadius:'4px',padding:'12px'}}>
                  <div style={{fontSize:'22px',fontWeight:700,color:s.c}}>{s.v}</div>
                  <div style={{fontSize:'10px',color:'#555555',marginTop:'3px'}}>{s.l}</div>
                </div>
              ))}
            </div>

            {/* Station grid */}
            <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:'10px',marginBottom:'20px'}}>
              {stations.map(st=>{
                const c = st.status==='ringing'?'#ef4444':st.status==='online'?'#22c55e':st.status==='busy'?'#f59e0b':'#555555';
                return (
                  <div key={st.id} style={{
                    background:'#111',border:`1px solid ${st.status==='ringing'?'rgba(239,68,68,0.3)':'rgba(255,255,255,0.06)'}`,
                    borderRadius:'4px',padding:'14px',
                  }}>
                    <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'8px'}}>
                      <div style={{width:'8px',height:'8px',borderRadius:'50%',background:c,boxShadow:`0 0 5px ${c}80`,flexShrink:0}}/>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:'12px',fontWeight:600,color:'#e5e5e5',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{st.name}</div>
                        <div style={{fontSize:'10px',color:'#555555'}}>{st.site} · {st.type}</div>
                      </div>
                      <span style={{fontSize:'9px',padding:'2px 7px',borderRadius:'6px',fontWeight:700,background:`${c}18`,color:c,textTransform:'uppercase',flexShrink:0}}>{st.status}</span>
                    </div>
                    <div style={{fontSize:'10px',color:'#555555',marginBottom:'10px'}}>Last call: {st.lastCall}</div>
                    <div style={{display:'flex',gap:'6px'}}>
                      {st.status==='ringing' && <button style={{flex:1,padding:'5px',borderRadius:'6px',border:'none',background:'rgba(34,197,94,0.15)',color:'#22c55e',fontSize:'10px',fontWeight:700,cursor:'pointer'}} onClick={()=>showNotif(`Answered: ${st.name}`)}>📞 Answer</button>}
                      {st.status!=='ringing' && <button style={{flex:1,padding:'5px',borderRadius:'6px',border:'1px solid rgba(59,130,246,0.2)',background:'rgba(59,130,246,0.08)',color:'#3b82f6',fontSize:'10px',fontWeight:600,cursor:'pointer'}} onClick={()=>showNotif(`Calling ${st.name}...`)}>📞 Call</button>}
                      <button style={{flex:1,padding:'5px',borderRadius:'6px',border:'1px solid #1a1a1a',background:'#111111',color:'#a0a0a0',fontSize:'10px',fontWeight:600,cursor:'pointer'}} onClick={()=>showNotif(`Opening ${st.name} video...`)}>📹 View</button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Call log */}
            <div style={{fontSize:'11px',fontWeight:600,color:'#a0a0a0',marginBottom:'8px'}}>Call Log — Today</div>
            {initialLoading ? (
              <div>
                {Array.from({length:4}).map((_,i) => (
                  <div key={i} style={{ display:'flex', gap:'14px', padding:'8px 12px', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                    <div className="skeleton" style={{ height:'12px', width:'130px' }} />
                    <div className="skeleton" style={{ height:'12px', width:'90px' }} />
                    <div className="skeleton" style={{ height:'12px', width:'50px' }} />
                    <div className="skeleton" style={{ height:'12px', width:'40px' }} />
                    <div className="skeleton" style={{ height:'20px', width:'60px', borderRadius:'4px' }} />
                  </div>
                ))}
              </div>
            ) : callLog.length === 0 ? (
              <EmptyState icon="📞" title="No intercom calls recorded" description="Call history from your Jacques or Aiphone intercom system will appear here." size="sm" />
            ) : (
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead>
                <tr style={{borderBottom:'1px solid #1a1a1a'}}>
                  {['Station','Site','Time','Duration','Status'].map(h=>(
                    <th key={h} style={{padding:'8px 12px',textAlign:'left',fontSize:'10px',fontWeight:700,color:'#555555',textTransform:'uppercase',letterSpacing:'0.05em'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {callLog.map(c=>(
                  <tr key={c.id} style={{borderBottom:'1px solid rgba(255,255,255,0.04)'}}
                    onMouseOver={e=>(e.currentTarget.style.background='#111')}
                    onMouseOut={e=>(e.currentTarget.style.background='transparent')}>
                    <td style={{padding:'8px 12px',fontSize:'12px',color:'#e5e5e5',fontWeight:500}}>{c.station}</td>
                    <td style={{padding:'8px 12px',fontSize:'11px',color:'#555555'}}>{c.site}</td>
                    <td style={{padding:'8px 12px',fontSize:'11px',color:'#555555',fontVariantNumeric:'tabular-nums'}}>{c.time}</td>
                    <td style={{padding:'8px 12px',fontSize:'11px',color:'#555555'}}>{c.duration}</td>
                    <td style={{padding:'8px 12px'}}>
                      <span style={{fontSize:'9px',padding:'2px 8px',borderRadius:'7px',fontWeight:700,
                        background:!c.answered?'rgba(239,68,68,0.1)':'rgba(34,197,94,0.1)',
                        color:!c.answered?'#ef4444':'#22c55e'}}>
                        {!c.answered?'Missed':'Answered'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            )}
            </>}
          </div>
        )}
      </div>
    </Layout>
  );
}
